import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { audioManager } from '../audio/AudioManager';

interface CutsceneKeyframe {
  time: number;
  text: string;
  camPos: THREE.Vector3;
  lookAt: THREE.Vector3;
  roll?: number; // camera Z rotation in radians (for tilting/falling)
  action?: (ctx: CutsceneContext) => void;
}

interface CutsceneContext {
  scene: THREE.Scene;
  objects: THREE.Object3D[];
  camera: THREE.PerspectiveCamera;
}

export class Cutscene {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private overlay: HTMLElement;
  private objects: THREE.Object3D[] = [];
  private origExposure: number;
  private composer!: EffectComposer;
  private cameraShake = { intensity: 0, decay: 0.92 };
  private dustParticles: THREE.Points | null = null;
  private activeAlarm: { stop: () => void } | null = null;
  private lastFootstepTime = 0;

  constructor(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer;
    this.overlay = document.getElementById('ui-overlay')!;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 200);
    this.origExposure = renderer.toneMappingExposure;
  }

  private setupPostProcessing(): void {
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    // Bloom only (lightweight) for glowing eyes and emissives
    const bloom = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.2, 0.3, 0.92
    );
    this.composer.addPass(bloom);

    this.composer.addPass(new OutputPass());
  }

  async playIntro(): Promise<void> {
    this.doorSmashed = false;
    this.zombieGrabbing = false;
    this.renderer.toneMappingExposure = 1.2;
    this.setupPostProcessing();
    this.buildLabScene();
    audioManager.init();
    this.activeAlarm = audioManager.startAlarm();
    await this.runKeyframes(this.getIntroKeyframes());
    this.stopCutsceneAudio();
    this.cleanup();
    this.renderer.toneMappingExposure = this.origExposure;
  }

  async playEscape(): Promise<void> {
    this.renderer.toneMappingExposure = 1.8;
    this.setupPostProcessing();
    this.buildEscapeScene();
    audioManager.init();
    await this.runKeyframes(this.getEscapeKeyframes());
    this.cleanup();
    this.renderer.toneMappingExposure = this.origExposure;
  }

  async playStageClear(stage: number): Promise<void> {
    this.renderer.toneMappingExposure = 1.5;
    this.setupPostProcessing();
    this.buildStageClearScene(stage);
    await this.runKeyframes(this.getStageClearKeyframes(stage));
    this.cleanup();
    this.renderer.toneMappingExposure = this.origExposure;
  }

  private buildStageClearScene(stage: number): void {
    const M = this.M.bind(this);
    const themes: Record<number, { sky: number; ground: number; fog: number; fogD: number; light: number }> = {
      2: { sky: 0x556688, ground: 0x555555, fog: 0x223344, fogD: 0.005, light: 0xddeeff },
      3: { sky: 0x556655, ground: 0x555555, fog: 0x223322, fogD: 0.008, light: 0xccddcc },
      4: { sky: 0x445544, ground: 0x555544, fog: 0x222211, fogD: 0.008, light: 0xbbcc99 },
      5: { sky: 0x556677, ground: 0x555555, fog: 0x223344, fogD: 0.008, light: 0xccddee },
      6: { sky: 0x556677, ground: 0x555555, fog: 0x334455, fogD: 0.006, light: 0xddeeff },
      7: { sky: 0x665544, ground: 0x555544, fog: 0x332211, fogD: 0.008, light: 0xddbb99 },
      8: { sky: 0x445566, ground: 0x555555, fog: 0x223344, fogD: 0.006, light: 0xccddee },
      9: { sky: 0x556655, ground: 0x555555, fog: 0x334433, fogD: 0.006, light: 0xddddcc },
      10: { sky: 0x664433, ground: 0x444444, fog: 0x332211, fogD: 0.008, light: 0xffaa77 },
    };
    const t = themes[stage] ?? themes[2]!;

    this.scene.fog = new THREE.FogExp2(t.fog, t.fogD);
    // Bright ambient so everything is visible
    this.scene.add(new THREE.AmbientLight(0x666666, 2.5));
    this.objects.push(this.scene.children[this.scene.children.length - 1]);
    // Hemisphere for natural fill
    const hemi = new THREE.HemisphereLight(0x8899aa, 0x444433, 1.5);
    this.add(hemi);

    // Sky
    const sky = new THREE.Mesh(new THREE.SphereGeometry(50, 12, 8), new THREE.MeshBasicMaterial({ color: t.sky, side: THREE.BackSide }));
    this.add(sky);

    // Ground
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(60, 60), M(t.ground, 0.9));
    ground.rotation.x = -Math.PI / 2;
    this.add(ground);

    // Main light (bright)
    const mainLight = new THREE.PointLight(t.light, 8, 40);
    mainLight.position.set(0, 6, -5);
    this.add(mainLight);
    // Extra fill light
    const fillLight = new THREE.PointLight(0xffffff, 4, 30);
    fillLight.position.set(-5, 4, 5);
    this.add(fillLight);

    switch (stage) {
      case 2: { // City → Hospital: 街を抜けて病院が見える
        // Extra lighting for visibility
        const fillLight1 = new THREE.PointLight(0xffffff, 5, 40);
        fillLight1.position.set(5, 6, 0);
        this.add(fillLight1);
        const fillLight2 = new THREE.PointLight(0xddeeff, 4, 30);
        fillLight2.position.set(15, 4, 0);
        this.add(fillLight2);
        const hemiLight = new THREE.HemisphereLight(0x667788, 0x222211, 2);
        this.add(hemiLight);

        // City buildings (both sides of road)
        const buildColors = [0x556677, 0x4a5a6a, 0x5a6a7a, 0x667788];
        for (let i = 0; i < 5; i++) {
          const bh = 3 + Math.random() * 5;
          const b = new THREE.Mesh(new THREE.BoxGeometry(2.5, bh, 2.5), M(buildColors[i % buildColors.length]));
          b.position.set(-8 + i * 4, bh / 2, -7);
          this.add(b);
        }
        for (let i = 0; i < 4; i++) {
          const bh = 2 + Math.random() * 4;
          const b = new THREE.Mesh(new THREE.BoxGeometry(2, bh, 2), M(buildColors[i % buildColors.length]));
          b.position.set(-5 + i * 5, bh / 2, 7);
          this.add(b);
        }

        // Road
        const road = new THREE.Mesh(new THREE.PlaneGeometry(35, 6), M(0x444444, 0.85));
        road.rotation.x = -Math.PI / 2; road.position.set(8, 0.01, 0);
        this.add(road);
        // Center line
        for (let rx = -4; rx < 25; rx += 3) {
          const line = new THREE.Mesh(new THREE.PlaneGeometry(1.5, 0.1), M(0xcccc66, 0.6));
          line.rotation.x = -Math.PI / 2; line.position.set(rx, 0.02, 0);
          this.add(line);
        }

        // Hospital building (white, large)
        const hospital = new THREE.Mesh(new THREE.BoxGeometry(10, 6, 8), M(0xeeeeee, 0.5));
        hospital.position.set(18, 3, 0);
        this.add(hospital);
        // Automatic glass doors (transparent, aligned with wall)
        for (const side of [-1, 1]) {
          const glassDoor = new THREE.Mesh(
            new THREE.BoxGeometry(0.05, 2.5, 0.85),
            new THREE.MeshStandardMaterial({
              color: 0x88bbdd, transparent: true, opacity: 0.3,
              roughness: 0.1, metalness: 0.5,
            })
          );
          glassDoor.position.set(12.85, 1.25, side * 0.45);
          this.add(glassDoor);
        }
        // Door frame (metal)
        const doorFrame = new THREE.Mesh(new THREE.BoxGeometry(0.06, 2.8, 0.06), M(0x888888, 0.3, 0.8));
        doorFrame.position.set(12.85, 1.4, 0);
        this.add(doorFrame);
        for (const side of [-1, 1]) {
          const sideFrame = new THREE.Mesh(new THREE.BoxGeometry(0.06, 2.8, 0.06), M(0x888888, 0.3, 0.8));
          sideFrame.position.set(12.85, 1.4, side * 0.9);
          this.add(sideFrame);
        }
        const topFrame = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.06, 1.9), M(0x888888, 0.3, 0.8));
        topFrame.position.set(12.85, 2.8, 0);
        this.add(topFrame);
        // Hospital windows
        for (let wy = 2; wy < 5.5; wy += 1.5) {
          for (let wz = -2.5; wz <= 2.5; wz += 1.5) {
            const win = new THREE.Mesh(new THREE.PlaneGeometry(0.8, 1), new THREE.MeshBasicMaterial({ color: 0x88bbdd }));
            win.position.set(12.99, wy, wz);
            win.rotation.y = Math.PI / 2;
            this.add(win);
          }
        }

        // Red cross on hospital wall
        const crossH = new THREE.Mesh(new THREE.BoxGeometry(0.06, 2.0, 0.5), new THREE.MeshBasicMaterial({ color: 0xff0000 }));
        crossH.position.set(12.95, 4.5, 0);
        this.add(crossH);
        const crossV = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.5, 2.0), new THREE.MeshBasicMaterial({ color: 0xff0000 }));
        crossV.position.set(12.95, 4.5, 0);
        this.add(crossV);
        // Red cross glow
        const crossLight = new THREE.PointLight(0xff2222, 2, 6);
        crossLight.position.set(12.5, 4.5, 0);
        this.add(crossLight);

        // Dead bodies on road and sidewalk
        const deadSkinMat = this.skinMat(0x6a7a5a);
        const deadClothMat = M(0x3a3a3a, 0.8);
        const deadPositions = [
          { x: 2, z: 1.5, ry: 0.5 }, { x: 6, z: -1, ry: 1.8 },
          { x: 9, z: 2, ry: -0.3 }, { x: 4, z: -2.5, ry: 2.5 },
          { x: 11, z: 1, ry: 1.2 }, { x: 0, z: -1.5, ry: 0.8 },
        ];
        for (const dp of deadPositions) {
          const dg = new THREE.Group();
          // Body
          const dbody = new THREE.Mesh(new THREE.BoxGeometry(0.4, 1.2, 0.25), Math.random() > 0.5 ? deadClothMat : deadSkinMat);
          dg.add(dbody);
          // Head
          const dhead = new THREE.Mesh(new THREE.SphereGeometry(0.12, 6, 5), deadSkinMat);
          dhead.position.y = 0.7;
          dg.add(dhead);
          // Lying on ground
          dg.position.set(dp.x, 0.12, dp.z);
          dg.rotation.x = Math.PI / 2;
          dg.rotation.z = dp.ry;
          this.add(dg);
          // Blood pool
          const pool = new THREE.Mesh(new THREE.CircleGeometry(0.3 + Math.random() * 0.2, 8), new THREE.MeshStandardMaterial({ color: 0x550000, roughness: 0.3 }));
          pool.rotation.x = -Math.PI / 2;
          pool.position.set(dp.x, 0.003, dp.z);
          this.add(pool);
        }

        // Wrecked car
        const carBody = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.8, 1), M(0x884422));
        carBody.position.set(7, 0.6, 3.5);
        carBody.rotation.y = 0.3;
        this.add(carBody);
        const carTop = new THREE.Mesh(new THREE.BoxGeometry(1, 0.5, 0.9), M(0x773311));
        carTop.position.set(7, 1.15, 3.5);
        this.add(carTop);

        break;
      }
      case 3: { // Hospital → Sewer: 病院の地下へ
        // Hospital interior walls
        for (const z of [-3, 3]) {
          const w = new THREE.Mesh(new THREE.BoxGeometry(20, 3, 0.2), M(0xdddddd, 0.7));
          w.position.set(0, 1.5, z); this.add(w);
        }
        // Manhole in floor
        const manhole = new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.6, 0.05, 12), M(0x555555, 0.5, 0.6));
        manhole.position.set(8, 0.03, 0); this.add(manhole);
        const manholeRing = new THREE.Mesh(new THREE.TorusGeometry(0.65, 0.05, 6, 12), M(0x444444, 0.4, 0.7));
        manholeRing.rotation.x = Math.PI / 2; manholeRing.position.set(8, 0.06, 0); this.add(manholeRing);
        // Green glow from below
        const sewerLight = new THREE.PointLight(0x44aa44, 3, 6);
        sewerLight.position.set(8, -0.5, 0); this.add(sewerLight);
        break;
      }
      case 4: { // Sewer → Military base: 下水道を抜けて軍事基地
        // Tunnel walls
        for (const z of [-2, 2]) {
          const w = new THREE.Mesh(new THREE.BoxGeometry(25, 3, 0.3), M(0x3a3a2a));
          w.position.set(0, 1.5, z); this.add(w);
        }
        // Light at end of tunnel
        const endLight = new THREE.PointLight(0xffeedd, 8, 20);
        endLight.position.set(12, 2, 0); this.add(endLight);
        // Military fence visible outside
        for (let i = 0; i < 5; i++) {
          const post = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 3, 6), M(0x666666, 0.4, 0.6));
          post.position.set(15 + i * 2, 1.5, -4); this.add(post);
        }
        break;
      }
      case 5: { // Military → Subway
        // Military base behind (concrete walls, fence)
        const baseWall = new THREE.Mesh(new THREE.BoxGeometry(0.3, 3, 16), M(0x666666, 0.6, 0.15));
        baseWall.position.set(-8, 1.5, 0);
        this.add(baseWall);
        // Barbed wire on wall
        for (let bz = -6; bz <= 6; bz += 3) {
          const bWire = new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.015, 4, 16), M(0x999999, 0.3, 0.7));
          bWire.position.set(-8, 3.1, bz);
          this.add(bWire);
        }

        // Street / road area
        const street = new THREE.Mesh(new THREE.PlaneGeometry(35, 10), M(0x444444, 0.85));
        street.rotation.x = -Math.PI / 2; street.position.set(10, 0.01, 0);
        this.add(street);
        // Sidewalk
        const sidewalkL = new THREE.Mesh(new THREE.BoxGeometry(30, 0.15, 2), M(0x666666, 0.7));
        sidewalkL.position.set(10, 0.08, -5.5);
        this.add(sidewalkL);
        const sidewalkR = new THREE.Mesh(new THREE.BoxGeometry(30, 0.15, 2), M(0x666666, 0.7));
        sidewalkR.position.set(10, 0.08, 5.5);
        this.add(sidewalkR);

        // City buildings on both sides
        const bColors = [0x667788, 0x556677, 0x778899];
        for (let i = 0; i < 5; i++) {
          const bh = 4 + Math.random() * 6;
          const b = new THREE.Mesh(new THREE.BoxGeometry(3, bh, 3), M(bColors[i % 3]));
          b.position.set(5 + i * 5, bh / 2, -9);
          this.add(b);
        }
        for (let i = 0; i < 4; i++) {
          const bh = 3 + Math.random() * 5;
          const b = new THREE.Mesh(new THREE.BoxGeometry(2.5, bh, 2.5), M(bColors[i % 3]));
          b.position.set(8 + i * 5, bh / 2, 9);
          this.add(b);
        }

        // === SUBWAY ENTRANCE (focal point) ===
        // Entrance structure (concrete box above ground)
        const entryBox = new THREE.Mesh(new THREE.BoxGeometry(5, 3, 4), M(0x777777, 0.6, 0.15));
        entryBox.position.set(20, 1.5, 0);
        this.add(entryBox);
        // Roof overhang
        const entryRoof = new THREE.Mesh(new THREE.BoxGeometry(6, 0.2, 5), M(0x666666, 0.5, 0.2));
        entryRoof.position.set(20, 3.1, 0);
        this.add(entryRoof);

        // Entrance opening (dark hole)
        const entryHole = new THREE.Mesh(new THREE.BoxGeometry(0.1, 2.5, 2.5), new THREE.MeshBasicMaterial({ color: 0x111111 }));
        entryHole.position.set(17.5, 1.25, 0);
        this.add(entryHole);

        // Stairs going down inside
        for (let i = 0; i < 6; i++) {
          const step = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.12, 0.35), M(0x888888, 0.5, 0.3));
          step.position.set(18.5 + i * 0.35, -i * 0.25, 0);
          this.add(step);
        }

        // Handrails
        for (const hz of [-1.1, 1.1]) {
          const handrail = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 4, 6), M(0xaaaa88, 0.3, 0.7));
          handrail.rotation.z = -0.3;
          handrail.position.set(19.5, 1.5, hz);
          this.add(handrail);
        }

        // Subway sign (blue with white text style)
        const subSign = new THREE.Mesh(new THREE.BoxGeometry(2.5, 0.7, 0.08), new THREE.MeshBasicMaterial({ color: 0x0044aa }));
        subSign.position.set(17.4, 2.6, 0);
        this.add(subSign);
        // "M" letter on sign
        const mLetter = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.4, 0.35), new THREE.MeshBasicMaterial({ color: 0xffffff }));
        mLetter.position.set(17.35, 2.6, 0);
        this.add(mLetter);

        // Round subway symbol above entrance
        const subCircle = new THREE.Mesh(new THREE.CircleGeometry(0.5, 16), new THREE.MeshBasicMaterial({ color: 0x0055cc }));
        subCircle.position.set(17.4, 3.5, 0);
        subCircle.rotation.y = Math.PI / 2;
        this.add(subCircle);
        const subCircleInner = new THREE.Mesh(new THREE.CircleGeometry(0.35, 16), new THREE.MeshBasicMaterial({ color: 0xffffff }));
        subCircleInner.position.set(17.38, 3.5, 0);
        subCircleInner.rotation.y = Math.PI / 2;
        this.add(subCircleInner);

        // Light from inside entrance (warm glow from below)
        const entryLight = new THREE.PointLight(0xffcc88, 3, 8);
        entryLight.position.set(18.5, 0.5, 0);
        this.add(entryLight);

        // Street lights
        for (const lx of [5, 15, 25]) {
          const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 5, 6), M(0x666666, 0.4, 0.5));
          pole.position.set(lx, 2.5, -4.5);
          this.add(pole);
          const lamp = new THREE.PointLight(0xffddaa, 2, 8);
          lamp.position.set(lx, 5, -4.5);
          this.add(lamp);
          const shade = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.1, 0.15, 6), M(0x888888));
          shade.position.set(lx, 5, -4.5);
          this.add(shade);
        }

        // Abandoned car on street
        const aCar = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.7, 1), M(0x885544));
        aCar.position.set(12, 0.5, 2);
        aCar.rotation.y = 0.15;
        this.add(aCar);
        const aCarTop = new THREE.Mesh(new THREE.BoxGeometry(1, 0.45, 0.9), M(0x774433));
        aCarTop.position.set(12, 1, 2);
        this.add(aCarTop);

        break;
      }
      case 6: { // Subway → Factory
        // Subway platform behind
        const platform = new THREE.Mesh(new THREE.BoxGeometry(12, 0.5, 3), M(0x555555));
        platform.position.set(-5, 0.25, -2);
        this.add(platform);
        // Tracks
        for (const tz of [-0.3, 0.3]) {
          const rail = new THREE.Mesh(new THREE.BoxGeometry(18, 0.05, 0.06), M(0x888888, 0.3, 0.8));
          rail.position.set(-5, 0.05, tz + 2);
          this.add(rail);
        }
        // Track ties
        for (let tx = -12; tx < 5; tx += 1) {
          const tie = new THREE.Mesh(new THREE.BoxGeometry(0.15, 0.04, 1.2), M(0x4a3a2a));
          tie.position.set(tx, 0.02, 2);
          this.add(tie);
        }

        // Road from subway exit to factory
        const factRoad = new THREE.Mesh(new THREE.PlaneGeometry(30, 6), M(0x444444, 0.85));
        factRoad.rotation.x = -Math.PI / 2; factRoad.position.set(15, 0.01, 0);
        this.add(factRoad);

        // Main factory building (far away)
        const factMain = new THREE.Mesh(new THREE.BoxGeometry(14, 8, 10), M(0x665544, 0.7, 0.1));
        factMain.position.set(30, 4, 0);
        this.add(factMain);
        // Factory roof (angled)
        const factRoof = new THREE.Mesh(new THREE.BoxGeometry(15, 0.3, 11), M(0x555544, 0.6));
        factRoof.position.set(30, 8.15, 0);
        factRoof.rotation.z = 0.05;
        this.add(factRoof);

        // Side building
        const sideBuild = new THREE.Mesh(new THREE.BoxGeometry(6, 5, 8), M(0x5a4a3a, 0.7));
        sideBuild.position.set(24, 2.5, -7);
        this.add(sideBuild);

        // Factory windows (dirty, some broken)
        for (let wy = 2; wy < 7; wy += 2.5) {
          for (let wz = -3; wz <= 3; wz += 2) {
            const isBroken = Math.random() > 0.6;
            const win = new THREE.Mesh(
              new THREE.PlaneGeometry(1, 1.5),
              new THREE.MeshBasicMaterial({
                color: isBroken ? 0x334433 : 0x667766,
                transparent: true, opacity: isBroken ? 0.3 : 0.5,
              })
            );
            win.position.set(23, wy, wz);
            win.rotation.y = Math.PI / 2;
            this.add(win);
          }
        }

        // Large doors (industrial entrance)
        const lDoor = new THREE.Mesh(new THREE.BoxGeometry(0.1, 4, 3), M(0x776655, 0.6, 0.2));
        lDoor.position.set(23, 2, 0);
        this.add(lDoor);
        // Door frame
        const dFrame = new THREE.Mesh(new THREE.BoxGeometry(0.15, 4.5, 0.2), M(0x888888, 0.4, 0.5));
        dFrame.position.set(23, 2.25, -1.6);
        this.add(dFrame);
        const dFrame2 = new THREE.Mesh(new THREE.BoxGeometry(0.15, 4.5, 0.2), M(0x888888, 0.4, 0.5));
        dFrame2.position.set(23, 2.25, 1.6);
        this.add(dFrame2);

        // Smokestacks (2 tall chimneys)
        for (const sz of [-3, 3]) {
          const chimney = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.7, 10, 8), M(0x777766, 0.5, 0.2));
          chimney.position.set(34, 5, sz);
          this.add(chimney);
          // Chimney cap
          const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.5, 0.5, 8), M(0x666655));
          cap.position.set(34, 10.25, sz);
          this.add(cap);
        }

        // Rusty pipes on outside
        for (let py = 2; py < 7; py += 2.5) {
          const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 8, 6), M(0x885533, 0.7, 0.2));
          pipe.rotation.z = Math.PI / 2;
          pipe.position.set(27, py, 5.1);
          this.add(pipe);
        }

        // Old truck parked outside
        const truckBody = new THREE.Mesh(new THREE.BoxGeometry(2.5, 1.2, 1.5), M(0x556655, 0.7));
        truckBody.position.set(18, 0.8, 4);
        truckBody.rotation.y = 0.2;
        this.add(truckBody);
        const truckCab = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1.4), M(0x445544, 0.7));
        truckCab.position.set(16.8, 1.1, 4);
        this.add(truckCab);
        // Wheels
        for (const wx of [17.5, 19]) {
          for (const wz of [3.3, 4.7]) {
            const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.15, 8), M(0x222222));
            wheel.rotation.x = Math.PI / 2;
            wheel.position.set(wx, 0.3, wz);
            this.add(wheel);
          }
        }

        // Extra lighting
        const facLight = new THREE.PointLight(0xffddaa, 5, 25);
        facLight.position.set(20, 5, 0);
        this.add(facLight);

        break;
      }
      case 7: { // Factory → Dark forest
        // Factory wall behind
        const wall = new THREE.Mesh(new THREE.BoxGeometry(12, 4, 0.3), M(0x554433));
        wall.position.set(-8, 2, 0); this.add(wall);
        // Trees ahead
        for (let i = 0; i < 8; i++) {
          const tx = 4 + i * 3 + Math.random() * 2;
          const tz = -5 + Math.random() * 10;
          const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.2, 4, 6), M(0x3a2a1a));
          trunk.position.set(tx, 2, tz); this.add(trunk);
          const canopy = new THREE.Mesh(new THREE.SphereGeometry(1.5, 6, 5), M(0x1a3a1a));
          canopy.position.set(tx, 5, tz); this.add(canopy);
        }
        // Fog is thicker
        this.scene.fog = new THREE.FogExp2(0x0a1a0a, 0.04);
        break;
      }
      case 8: { // Dark forest → Airport
        // Trees behind (forest edge)
        for (let i = 0; i < 6; i++) {
          const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.2, 4, 6), M(0x3a2a1a));
          trunk.position.set(-10 + i * 3, 2, -4 + Math.random() * 8);
          this.add(trunk);
          const canopy = new THREE.Mesh(new THREE.SphereGeometry(1.2, 6, 5), M(0x2a4a2a));
          canopy.position.set(-10 + i * 3, 4.5, -4 + Math.random() * 8);
          this.add(canopy);
        }

        // Road to airport
        const apRoad = new THREE.Mesh(new THREE.PlaneGeometry(40, 8), M(0x444444, 0.85));
        apRoad.rotation.x = -Math.PI / 2; apRoad.position.set(15, 0.01, 0);
        this.add(apRoad);
        // Road markings
        for (let rx = 2; rx < 35; rx += 4) {
          const line = new THREE.Mesh(new THREE.PlaneGeometry(2, 0.12), M(0xcccc66));
          line.rotation.x = -Math.PI / 2; line.position.set(rx, 0.02, 0);
          this.add(line);
        }

        // Airport terminal (far away, large)
        const terminal = new THREE.Mesh(new THREE.BoxGeometry(22, 5, 12), M(0xaaaaaa, 0.5, 0.2));
        terminal.position.set(30, 2.5, 0);
        this.add(terminal);
        // Terminal roof overhang
        const roof = new THREE.Mesh(new THREE.BoxGeometry(24, 0.3, 14), M(0x888888, 0.4, 0.3));
        roof.position.set(30, 5.15, 0);
        this.add(roof);
        // Second floor
        const floor2 = new THREE.Mesh(new THREE.BoxGeometry(20, 4, 10), M(0x999999, 0.5, 0.2));
        floor2.position.set(30, 7, 0);
        this.add(floor2);

        // Glass facade (front face)
        for (let gz = -4; gz <= 4; gz += 2) {
          for (let gy = 1; gy <= 4; gy += 1.5) {
            const glass = new THREE.Mesh(
              new THREE.PlaneGeometry(1.5, 1.2),
              new THREE.MeshBasicMaterial({ color: 0x88bbdd, transparent: true, opacity: 0.5 })
            );
            glass.position.set(19, gy, gz);
            glass.rotation.y = Math.PI / 2;
            this.add(glass);
          }
        }
        // Upper floor windows
        for (let gz = -3; gz <= 3; gz += 2) {
          const glass = new THREE.Mesh(
            new THREE.PlaneGeometry(1.5, 1.5),
            new THREE.MeshBasicMaterial({ color: 0x6699bb, transparent: true, opacity: 0.4 })
          );
          glass.position.set(20, 7, gz);
          glass.rotation.y = Math.PI / 2;
          this.add(glass);
        }

        // Entrance canopy
        const canopyRoof = new THREE.Mesh(new THREE.BoxGeometry(4, 0.15, 6), M(0x777777, 0.4, 0.4));
        canopyRoof.position.set(17, 3.5, 0);
        this.add(canopyRoof);
        // Canopy pillars
        for (const cz of [-2.5, 2.5]) {
          const pillar = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 3.5, 6), M(0x888888, 0.3, 0.6));
          pillar.position.set(17, 1.75, cz);
          this.add(pillar);
        }

        // Control tower (tall, further back)
        const towerBase = new THREE.Mesh(new THREE.CylinderGeometry(1.2, 1.5, 12, 8), M(0x777777, 0.5, 0.3));
        towerBase.position.set(38, 6, -8);
        this.add(towerBase);
        // Tower cabin
        const towerCabin = new THREE.Mesh(new THREE.CylinderGeometry(2.5, 1.5, 3, 8), M(0x666666, 0.4, 0.3));
        towerCabin.position.set(38, 13, -8);
        this.add(towerCabin);
        // Tower windows (360 glass)
        const towerGlass = new THREE.Mesh(
          new THREE.CylinderGeometry(2.6, 1.6, 2.5, 12, 1, true),
          new THREE.MeshBasicMaterial({ color: 0x88ccee, transparent: true, opacity: 0.4, side: THREE.DoubleSide })
        );
        towerGlass.position.set(38, 13, -8);
        this.add(towerGlass);

        // Parked airplane (simple silhouette)
        const fuselage = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.8, 8, 8), M(0xdddddd, 0.4, 0.2));
        fuselage.rotation.z = Math.PI / 2;
        fuselage.position.set(35, 1.5, 10);
        this.add(fuselage);
        // Wings
        const wing = new THREE.Mesh(new THREE.BoxGeometry(0.15, 10, 1), M(0xcccccc, 0.4, 0.2));
        wing.rotation.z = Math.PI / 2;
        wing.position.set(35, 1.8, 10);
        this.add(wing);
        // Tail
        const tail = new THREE.Mesh(new THREE.BoxGeometry(0.1, 2.5, 1), M(0xcccccc, 0.4, 0.2));
        tail.position.set(31, 3, 10);
        this.add(tail);

        // Extra light on airport
        const apLight = new THREE.PointLight(0xffffff, 5, 25);
        apLight.position.set(20, 5, 0);
        this.add(apLight);

        break;
      }
      case 9: { // Airport → Final research facility
        // Runway behind
        const runway = new THREE.Mesh(new THREE.PlaneGeometry(40, 8), M(0x444444, 0.85));
        runway.rotation.x = -Math.PI / 2; runway.position.set(-5, 0.01, 0);
        this.add(runway);
        // Runway markings
        for (let rx = -15; rx < 10; rx += 5) {
          const mark = new THREE.Mesh(new THREE.PlaneGeometry(3, 0.3), new THREE.MeshBasicMaterial({ color: 0xeeeeee }));
          mark.rotation.x = -Math.PI / 2; mark.position.set(rx, 0.02, 0);
          this.add(mark);
        }

        // Road from runway to facility
        const accessRoad = new THREE.Mesh(new THREE.PlaneGeometry(25, 5), M(0x3a3a3a, 0.85));
        accessRoad.rotation.x = -Math.PI / 2; accessRoad.position.set(18, 0.01, 0);
        this.add(accessRoad);

        // Perimeter fence
        for (let i = 0; i < 8; i++) {
          const fPost = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 3, 4), M(0x888888, 0.4, 0.6));
          fPost.position.set(12 + i * 2, 1.5, -6);
          this.add(fPost);
        }
        // Fence wire
        for (let fy = 0.5; fy < 2.8; fy += 0.6) {
          const wire = new THREE.Mesh(new THREE.BoxGeometry(16, 0.02, 0.02), M(0x999999, 0.3, 0.7));
          wire.position.set(19, fy, -6);
          this.add(wire);
        }
        // Barbed wire on top
        const barbed = new THREE.Mesh(new THREE.TorusGeometry(0.15, 0.02, 4, 20), M(0xaaaaaa, 0.3, 0.7));
        barbed.rotation.y = Math.PI / 2;
        barbed.position.set(19, 3, -6);
        this.add(barbed);

        // Main research facility (far, large, ominous)
        const facMain = new THREE.Mesh(new THREE.BoxGeometry(16, 7, 12), M(0x444455, 0.6, 0.15));
        facMain.position.set(32, 3.5, 0);
        this.add(facMain);
        // Side wing left
        const wingL = new THREE.Mesh(new THREE.BoxGeometry(8, 5, 6), M(0x3a3a4a, 0.6, 0.1));
        wingL.position.set(28, 2.5, -9);
        this.add(wingL);
        // Side wing right
        const wingR = new THREE.Mesh(new THREE.BoxGeometry(8, 5, 6), M(0x3a3a4a, 0.6, 0.1));
        wingR.position.set(28, 2.5, 9);
        this.add(wingR);

        // Facility windows (dark, some lit red)
        for (let wy = 2; wy < 6; wy += 2) {
          for (let wz = -4; wz <= 4; wz += 2) {
            const isRed = Math.random() > 0.6;
            const win = new THREE.Mesh(
              new THREE.PlaneGeometry(1.2, 1.4),
              new THREE.MeshBasicMaterial({ color: isRed ? 0xff3322 : 0x223344, transparent: true, opacity: isRed ? 0.6 : 0.4 })
            );
            win.position.set(24, wy, wz);
            win.rotation.y = Math.PI / 2;
            this.add(win);
          }
        }

        // Entrance gate
        const gateL = new THREE.Mesh(new THREE.BoxGeometry(0.2, 4, 0.2), M(0x666666, 0.4, 0.6));
        gateL.position.set(22, 2, -2);
        this.add(gateL);
        const gateR = new THREE.Mesh(new THREE.BoxGeometry(0.2, 4, 0.2), M(0x666666, 0.4, 0.6));
        gateR.position.set(22, 2, 2);
        this.add(gateR);
        const gateTop = new THREE.Mesh(new THREE.BoxGeometry(0.15, 0.15, 4.4), M(0x666666, 0.4, 0.6));
        gateTop.position.set(22, 4, 0);
        this.add(gateTop);

        // Biohazard signs (yellow with symbol)
        for (const sz of [-2, 2]) {
          const sign = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.8, 0.05), new THREE.MeshBasicMaterial({ color: 0xffcc00 }));
          sign.position.set(22.1, 3, sz * 0.8);
          this.add(sign);
          // Triangle warning
          const tri = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.4, 0.06), new THREE.MeshBasicMaterial({ color: 0x222222 }));
          tri.position.set(22.08, 3, sz * 0.8);
          this.add(tri);
        }

        // Red warning lights on facility
        for (let i = 0; i < 4; i++) {
          const wl = new THREE.PointLight(0xff0000, 3, 10);
          wl.position.set(24, 6.5, -3 + i * 2);
          this.add(wl);
          // Light fixture
          const fixture = new THREE.Mesh(
            new THREE.SphereGeometry(0.1, 6, 4),
            new THREE.MeshBasicMaterial({ color: 0xff0000 })
          );
          fixture.position.set(24, 7, -3 + i * 2);
          this.add(fixture);
        }

        // Satellite dishes on roof
        for (const dz of [-3, 3]) {
          const dish = new THREE.Mesh(new THREE.SphereGeometry(0.8, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2), M(0xaaaaaa, 0.3, 0.5));
          dish.position.set(32, 7.5, dz);
          dish.rotation.x = -0.3;
          this.add(dish);
          const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1, 4), M(0x888888, 0.3, 0.6));
          pole.position.set(32, 7, dz);
          this.add(pole);
        }

        // Extra lighting
        const facLight = new THREE.PointLight(0xffffff, 5, 25);
        facLight.position.set(20, 5, 0);
        this.add(facLight);
        const redGlow = new THREE.PointLight(0xff4422, 4, 20);
        redGlow.position.set(28, 3, 0);
        this.add(redGlow);

        break;
      }
      case 10: { // Final facility → Ending
        // Destroyed facility
        for (let i = 0; i < 5; i++) {
          const debris = new THREE.Mesh(new THREE.BoxGeometry(1 + Math.random() * 2, 0.5 + Math.random(), 1 + Math.random() * 2), M(0x444444));
          debris.position.set(-5 + i * 3, Math.random() * 0.5, -3 + Math.random() * 6);
          debris.rotation.set(Math.random() * 0.3, Math.random(), Math.random() * 0.2);
          this.add(debris);
        }
        // Sunrise sky
        const sunrise = new THREE.Mesh(new THREE.SphereGeometry(50, 12, 8), new THREE.MeshBasicMaterial({ color: 0xff8844, side: THREE.BackSide }));
        this.add(sunrise);
        // Sun
        const sunGlow = new THREE.Mesh(new THREE.SphereGeometry(3, 12, 8), new THREE.MeshBasicMaterial({ color: 0xffdd88 }));
        sunGlow.position.set(20, 8, -15); this.add(sunGlow);
        const sunLight = new THREE.PointLight(0xffdd88, 10, 50);
        sunLight.position.set(20, 8, -15); this.add(sunLight);
        break;
      }
    }

    this.addDustParticles();
  }

  private getStageClearKeyframes(stage: number): CutsceneKeyframe[] {
    const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
    const bossNames: Record<number, string> = {
      2: 'Mother Zombie', 3: 'Armored Beast', 4: 'Toxic King',
      5: 'Cyber Zombie', 6: 'Hive Mind', 7: 'Flesh Golem',
      8: 'Shadow Stalker', 9: 'General Dead', 10: 'Apocalypse Zombie',
    };
    const texts: Record<number, string[]> = {
      2: ['市街地を制圧した', '前方に病院が見える...', 'あの中に生存者がいるかもしれない', '病院へ向かう...'],
      3: ['病院を脱出した', '地下への入口を発見した', '下水道を通って先へ進むしかない', '暗闇の中へ...'],
      4: ['下水道を抜けた', '軍事基地のフェンスが見える', 'まだ使える武器があるかもしれない', '基地へ潜入する...'],
      5: ['基地を制圧した', '地下鉄の入口を発見', '線路沿いに先へ進めるはずだ', '地下へ降りる...'],
      6: ['地下鉄を脱出した', '遠くに廃工場が見える', 'あそこに何かがいる...', '工場へ向かう...'],
      7: ['工場を破壊した', '目の前に暗い森が広がっている', '森の向こうに何かの施設がある', '森の中へ...'],
      8: ['森を抜けた', '空港ターミナルが見える', 'まだ飛行機は残っているか...', '空港へ...'],
      9: ['空港を確保した', '最終研究施設を発見', 'ここが全ての元凶だ', '最後の戦いへ...'],
      10: ['全てが終わった', '元凶のウイルスは消滅した', '長い戦いがようやく終わりを迎える', '世界に再び光が戻る...'],
    };
    const t = texts[stage] ?? ['STAGE CLEAR', '', '', ''];
    const bn = bossNames[stage] ?? 'BOSS';

    if (stage === 10) {
      // Special ending — longer, more dramatic
      return [
        { time: 0, text: `${bn} 撃破`, camPos: v(0, 1.5, 5), lookAt: v(0, 0.5, 0) },
        { time: 3, text: t[0], camPos: v(-3, 2, 3), lookAt: v(0, 1, 0) },
        { time: 6, text: t[1], camPos: v(0, 2, 0), lookAt: v(20, 8, -15) },
        { time: 9, text: t[2], camPos: v(2, 3, -2), lookAt: v(20, 8, -15) },
        { time: 12, text: t[3], camPos: v(0, 4, -5), lookAt: v(20, 8, -15) },
        { time: 15, text: 'THE END', camPos: v(0, 5, -8), lookAt: v(20, 9, -15) },
        { time: 18, text: '', camPos: v(0, 6, -10), lookAt: v(20, 10, -15) },
      ];
    }

    return [
      { time: 0, text: `${bn} 撃破`, camPos: v(-5, 1.6, 3), lookAt: v(5, 1.5, 0) },
      { time: 3, text: t[0], camPos: v(0, 1.6, 2), lookAt: v(10, 1.5, 0) },
      { time: 6, text: t[1], camPos: v(4, 1.8, 1), lookAt: v(12, 2, 0) },
      { time: 9, text: t[2], camPos: v(6, 2, -1), lookAt: v(15, 2, 0) },
      { time: 12, text: t[3], camPos: v(8, 2.5, 0), lookAt: v(18, 2, 0) },
      { time: 15, text: '', camPos: v(10, 3, 0), lookAt: v(20, 2.5, 0) },
    ];
  }

  // ==================== MATERIAL HELPERS ====================
  private M(color: number, rough = 0.8, metal = 0): THREE.MeshStandardMaterial {
    return new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: metal });
  }

  private skinMat(color: number): THREE.MeshStandardMaterial {
    return new THREE.MeshStandardMaterial({
      color,
      roughness: 0.7,
      metalness: 0.0,
    });
  }

  private wetFloorMat(): THREE.MeshStandardMaterial {
    return new THREE.MeshStandardMaterial({
      color: 0x3a3a3a,
      roughness: 0.85,
      metalness: 0.0,
    });
  }

  // ==================== LAB SCENE ====================
  private buildLabScene(): void {
    this.scene.fog = new THREE.FogExp2(0x080810, 0.012);

    // Ambient light — enough to see floor and walls
    this.scene.add(new THREE.AmbientLight(0x445566, 1.0));
    this.objects.push(this.scene.children[this.scene.children.length - 1]);

    // Hemisphere light for fill (sky + ground bounce)
    const hemi = new THREE.HemisphereLight(0x334455, 0x222211, 0.5);
    this.add(hemi);

    // ---- Floor (wet, reflective) ----
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(60, 8), this.wetFloorMat());
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.add(floor);

    // Tile grout lines
    const groutMat = this.M(0x252525, 0.95);
    for (let x = -28; x <= 28; x += 2) {
      const line = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.003, 8), groutMat);
      line.position.set(x, 0.001, 0);
      this.add(line);
    }
    for (let z = -3; z <= 3; z += 2) {
      const line = new THREE.Mesh(new THREE.BoxGeometry(60, 0.003, 0.03), groutMat);
      line.position.set(0, 0.001, z);
      this.add(line);
    }

    // Ceiling (stained, darker patches)
    const ceilMat = this.M(0x161616, 0.95);
    const ceil = new THREE.Mesh(new THREE.PlaneGeometry(60, 8), ceilMat);
    ceil.rotation.x = Math.PI / 2; ceil.position.y = 3.5;
    this.add(ceil);


    // Walls with panel detail
    for (const z of [-4, 4]) {
      const wallMat = this.M(0x2c2c30, 0.85, 0.05);
      const wall = new THREE.Mesh(new THREE.BoxGeometry(60, 3.5, 0.15), wallMat);
      wall.position.set(0, 1.75, z);
      wall.receiveShadow = true;
      this.add(wall);

      // Wall panels
      for (let x = -25; x <= 25; x += 3) {
        const panel = new THREE.Mesh(
          new THREE.BoxGeometry(2.2, 2.5, 0.02),
          this.M(0x353840, 0.7, 0.08)
        );
        panel.position.set(x, 1.5, z - Math.sign(z) * 0.08);
        this.add(panel);

      }

      // Baseboard
      const baseboard = new THREE.Mesh(
        new THREE.BoxGeometry(60, 0.12, 0.04),
        this.M(0x1a1a1e, 0.7, 0.1)
      );
      baseboard.position.set(0, 0.06, z - Math.sign(z) * 0.06);
      this.add(baseboard);
    }

    // Ceiling lights with fixtures (shadow-casting SpotLights)
    for (let x = -24; x <= 24; x += 6) {
      const isOn = Math.random() > 0.25;

      // Light housing
      const housing = new THREE.Mesh(
        new THREE.BoxGeometry(1.8, 0.08, 0.35),
        this.M(0x888888, 0.4, 0.6)
      );
      housing.position.set(x, 3.46, 0);
      this.add(housing);

      // Fluorescent tubes (2 per fixture)
      for (const tz of [-0.06, 0.06]) {
        const tubeMat = new THREE.MeshStandardMaterial({
          color: isOn ? 0xddeeff : 0x222222,
          emissive: isOn ? 0xaabbdd : 0x000000,
          emissiveIntensity: isOn ? 2.5 : 0,
        });
        const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 1.6, 8), tubeMat);
        tube.rotation.z = Math.PI / 2;
        tube.position.set(x, 3.42, tz);
        tube.userData.isLight = true;
        tube.userData.isOn = isOn;
        this.add(tube);
      }

      if (isOn) {
        const pl = new THREE.PointLight(0xccddff, 3, 10);
        pl.position.set(x, 3.3, 0);
        if (Math.random() > 0.5) pl.userData.isFlicker = true;
        this.add(pl);
      }
    }

    // Emergency red lights
    for (let x = -22; x <= 22; x += 8) {
      for (const z of [-3.9, 3.9]) {
        const redLight = new THREE.PointLight(0xff1500, 2, 8);
        redLight.position.set(x, 3.2, z);
        redLight.userData.isEmergency = true;
        this.add(redLight);

        // Fixture
        const fix = new THREE.Mesh(
          new THREE.BoxGeometry(0.1, 0.06, 0.06),
          new THREE.MeshStandardMaterial({ color: 0xff0000, emissive: 0xff0000, emissiveIntensity: 1 })
        );
        fix.position.set(x, 3.3, z);
        this.add(fix);
      }
    }

    // Pipes along ceiling (with brackets)
    for (const z of [-3.5, 3.5]) {
      const pipeMat = this.M(0x555555, 0.35, 0.75);
      const pipe = new THREE.Mesh(
        new THREE.CylinderGeometry(0.06, 0.06, 55, 12),
        pipeMat
      );
      pipe.rotation.z = Math.PI / 2;
      pipe.position.set(0, 3.3, z);
      this.add(pipe);

    }

    // Secondary pipes (thinner, with condensation drips)
    const thinPipeMat = this.M(0x607060, 0.5, 0.5);
    const thinPipe = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.03, 55, 8), thinPipeMat
    );
    thinPipe.rotation.z = Math.PI / 2;
    thinPipe.position.set(0, 3.1, -3.7);
    this.add(thinPipe);

    // Lab desks with equipment
    for (let x = -20; x <= 18; x += 5) {
      const side = x % 10 === 0 ? -2.5 : 2.5;
      this.add(this.makeDesk(x, side));

      if (Math.random() > 0.3) {
        const isOn = Math.random() > 0.4;
        // Monitor with bezel
        const monGroup = new THREE.Group();
        const bezel = new THREE.Mesh(
          new THREE.BoxGeometry(0.48, 0.38, 0.03),
          this.M(0x111111, 0.6, 0.3)
        );
        const screen = new THREE.Mesh(
          new THREE.PlaneGeometry(0.42, 0.32),
          new THREE.MeshStandardMaterial({
            color: isOn ? 0x0a2215 : 0x050505,
            emissive: isOn ? 0x11aa33 : 0x000000,
            emissiveIntensity: isOn ? 1.0 : 0,
          })
        );
        screen.position.z = -0.016;
        monGroup.add(bezel);
        monGroup.add(screen);
        // Stand
        const stand = new THREE.Mesh(
          new THREE.BoxGeometry(0.04, 0.12, 0.04),
          this.M(0x111111, 0.5, 0.4)
        );
        stand.position.y = -0.25;
        monGroup.add(stand);
        monGroup.position.set(x, 1.2, side);
        this.add(monGroup);

        // Screen glow
        if (isOn) {
          const glow = new THREE.PointLight(0x22aa44, 0.5, 2);
          glow.position.set(x, 1.2, side - 0.3);
          this.add(glow);
        }
      }

      // Lab equipment — beaker with dripping green liquid
      if (Math.random() > 0.4) {
        const bx = x + 0.5;
        const bz = side;
        const tiltAngle = 0.25 + Math.random() * 0.15; // tilted, spilling
        const tiltDir = Math.random() > 0.5 ? 1 : -1;

        const greenMat = new THREE.MeshStandardMaterial({
          color: 0x33ff66, emissive: 0x22aa44, emissiveIntensity: 0.3,
          transparent: true, opacity: 0.6,
        });

        // Beaker (tilted as if knocked over partially)
        const beaker = new THREE.Mesh(
          new THREE.CylinderGeometry(0.03, 0.05, 0.15, 10),
          new THREE.MeshStandardMaterial({
            color: 0x88ccff, transparent: true, opacity: 0.3,
            roughness: 0.05,
          })
        );
        beaker.position.set(bx, 1.0, bz);
        beaker.rotation.z = tiltDir * tiltAngle;
        this.add(beaker);

        // Liquid remaining inside (half full, tilted with beaker)
        const liquid = new THREE.Mesh(
          new THREE.CylinderGeometry(0.02, 0.04, 0.05, 8),
          greenMat
        );
        liquid.position.set(bx, 0.96, bz);
        liquid.rotation.z = tiltDir * tiltAngle;
        this.add(liquid);

        // Drip stream from beaker lip down to desk surface
        const dripX = bx + tiltDir * 0.05;
        const stream = new THREE.Mesh(
          new THREE.CylinderGeometry(0.004, 0.006, 0.08, 5),
          greenMat
        );
        stream.position.set(dripX, 0.95, bz);
        this.add(stream);

        // Puddle on desk surface
        const deskPuddle = new THREE.Mesh(
          new THREE.CircleGeometry(0.06, 10),
          greenMat
        );
        deskPuddle.rotation.x = -Math.PI / 2;
        deskPuddle.position.set(dripX, 0.91, bz);
        this.add(deskPuddle);

        // Trail running to desk edge
        const trailLen = 0.1 + Math.random() * 0.1;
        const trail = new THREE.Mesh(
          new THREE.PlaneGeometry(0.015, trailLen),
          greenMat
        );
        trail.rotation.x = -Math.PI / 2;
        trail.position.set(dripX + tiltDir * trailLen * 0.4, 0.91, bz);
        trail.rotation.z = tiltDir * 0.2;
        this.add(trail);

        // Drip falling from desk edge to floor (vertical stream)
        const edgeX = dripX + tiltDir * trailLen * 0.7;
        const fallStream = new THREE.Mesh(
          new THREE.CylinderGeometry(0.003, 0.005, 0.88, 4),
          greenMat
        );
        fallStream.position.set(edgeX, 0.46, bz);
        this.add(fallStream);

        // Small drip drops mid-fall
        for (let d = 0; d < 2; d++) {
          const drop = new THREE.Mesh(
            new THREE.SphereGeometry(0.006, 5, 4),
            greenMat
          );
          drop.scale.set(1, 1.5, 1);
          drop.position.set(edgeX + (Math.random() - 0.5) * 0.01, 0.3 + d * 0.25, bz);
          this.add(drop);
        }

        // Pool on floor
        const floorPool = new THREE.Mesh(
          new THREE.CircleGeometry(0.08 + Math.random() * 0.06, 10),
          greenMat
        );
        floorPool.rotation.x = -Math.PI / 2;
        floorPool.position.set(edgeX, 0.008, bz);
        this.add(floorPool);

        // Smaller splatter spots around pool
        for (let sp = 0; sp < 3; sp++) {
          const splat = new THREE.Mesh(
            new THREE.CircleGeometry(0.015 + Math.random() * 0.02, 6),
            greenMat
          );
          splat.rotation.x = -Math.PI / 2;
          splat.position.set(
            edgeX + (Math.random() - 0.5) * 0.12,
            0.007,
            bz + (Math.random() - 0.5) * 0.1
          );
          this.add(splat);
        }

        // Green glow on the puddle
        const grnGlow = new THREE.PointLight(0x33ff66, 0.4, 2);
        grnGlow.position.set(edgeX, 0.15, bz);
        this.add(grnGlow);
      }
    }

    // Blood stains (wet, reflective pools)
    const bloodMat = new THREE.MeshStandardMaterial({
      color: 0x660808, roughness: 0.2, metalness: 0.05,
      emissive: 0x220000, emissiveIntensity: 0.15,
    });
    const bloodSpots: [number, number][] = [
      [-3, 0.5], [0, -1], [5, 1], [-8, 0], [8, -0.5], [3, -2], [10, 0], [-1, 2],
    ];
    for (const [bx, bz] of bloodSpots) {
      const size = 0.3 + Math.random() * 0.6;
      const blood = new THREE.Mesh(new THREE.CircleGeometry(size, 16), bloodMat);
      blood.rotation.x = -Math.PI / 2;
      blood.position.set(bx, 0.008, bz);
      this.add(blood);

      // Splatter trails (irregular)
      for (let j = 0; j < 3; j++) {
        const trail = new THREE.Mesh(
          new THREE.PlaneGeometry(0.03 + Math.random() * 0.04, 0.15 + Math.random() * 0.2),
          bloodMat
        );
        trail.rotation.x = -Math.PI / 2;
        trail.rotation.z = Math.random() * Math.PI * 2;
        trail.position.set(
          bx + (Math.random() - 0.5) * 0.8,
          0.008,
          bz + (Math.random() - 0.5) * 0.8
        );
        this.add(trail);
      }
    }

    // Blood drip down walls
    const wallBloodMat = new THREE.MeshStandardMaterial({
      color: 0x550505, roughness: 0.4, transparent: true, opacity: 0.7,
    });
    for (let i = 0; i < 6; i++) {
      const drip = new THREE.Mesh(
        new THREE.PlaneGeometry(0.04, 0.3 + Math.random() * 0.8),
        wallBloodMat
      );
      const z = Math.random() > 0.5 ? -3.92 : 3.92;
      drip.position.set(-10 + Math.random() * 25, 0.8 + Math.random() * 1.5, z);
      drip.rotation.y = z > 0 ? Math.PI : 0;
      this.add(drip);
    }

    // Overturned furniture
    this.addChairOnSide(-5, 0.5);
    this.addTippedCart(2, -1);
    this.addFlippedDesk(-2, 1.5);

    // Scattered papers (crumpled effect)
    for (let i = 0; i < 10; i++) {
      const paper = new THREE.Mesh(
        new THREE.PlaneGeometry(0.15, 0.2),
        this.M(0xddddc8, 0.92)
      );
      paper.rotation.x = -Math.PI / 2 + (Math.random() - 0.5) * 0.3;
      paper.rotation.z = Math.random() * Math.PI;
      paper.position.set(-8 + Math.random() * 16, 0.01 + Math.random() * 0.03, -2 + Math.random() * 4);
      this.add(paper);
    }

    // Broken glass shards (refractive)
    const shardMat = new THREE.MeshStandardMaterial({
      color: 0xaaddff, transparent: true, opacity: 0.45,
    });
    for (let i = 0; i < 18; i++) {
      const pts = [
        new THREE.Vector3(0, 0, 0),
        new THREE.Vector3(0.04 + Math.random() * 0.08, 0, 0.015),
        new THREE.Vector3(0.02, 0, 0.05 + Math.random() * 0.06),
      ];
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      geo.computeVertexNormals();
      const shard = new THREE.Mesh(geo, shardMat);
      shard.position.set(-8 + Math.random() * 16, 0.003, -2 + Math.random() * 4);
      shard.rotation.y = Math.random() * Math.PI * 2;
      this.add(shard);
    }

    // Dust particles floating in light beams
    this.addDustParticles();

    // Dead researchers in the corridor
    this.addDeadResearcher(12, 1.5, 'side', 0.3);
    this.addDeadResearcher(15, -1.5, 'face_down', 2.1);
    this.addDeadResearcher(18, 0.5, 'slumped_wall', 0);

    // ===== BOSS ROOM (sealed room at end of corridor) =====
    const heavyWallMat = this.M(0x3a3a3e, 0.65, 0.35);

    // Wall left of door
    const wallL = new THREE.Mesh(new THREE.BoxGeometry(0.3, 3.5, 2.5), heavyWallMat);
    wallL.position.set(20, 1.75, -2.75);
    wallL.receiveShadow = true;
    this.add(wallL);
    // Wall right of door
    const wallR = new THREE.Mesh(new THREE.BoxGeometry(0.3, 3.5, 2.5), heavyWallMat);
    wallR.position.set(20, 1.75, 2.75);
    wallR.receiveShadow = true;
    this.add(wallR);
    // Wall above door
    const wallT = new THREE.Mesh(new THREE.BoxGeometry(0.3, 1.0, 8), heavyWallMat);
    wallT.position.set(20, 3.0, 0);
    this.add(wallT);

    // Heavy metal door (riveted)
    const doorMat = new THREE.MeshStandardMaterial({
      color: 0x4a4a4e, roughness: 0.35, metalness: 0.75,
    });
    const door = new THREE.Mesh(new THREE.BoxGeometry(0.15, 2.5, 1.6), doorMat);
    door.position.set(20, 1.25, 0);
    door.castShadow = false;
    this.add(door);



    // Door frame (thick metal)
    const frameMat = this.M(0x555560, 0.3, 0.8);
    for (const [w, h, d, x, y, z] of [
      [0.2, 2.6, 0.1, 20, 1.3, -0.85],
      [0.2, 2.6, 0.1, 20, 1.3, 0.85],
      [0.2, 0.1, 1.8, 20, 2.55, 0],
    ] as [number, number, number, number, number, number][]) {
      const frame = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), frameMat);
      frame.position.set(x, y, z);
      this.add(frame);
    }

    // Door handle (detailed)
    const handleGroup = new THREE.Group();
    const handlePlate = new THREE.Mesh(
      new THREE.BoxGeometry(0.04, 0.15, 0.06),
      this.M(0x888890, 0.25, 0.85)
    );
    const handleBar = new THREE.Mesh(
      new THREE.CylinderGeometry(0.012, 0.012, 0.1, 8),
      this.M(0x999999, 0.2, 0.9)
    );
    handleBar.rotation.z = Math.PI / 2;
    handleBar.position.z = -0.04;
    handleGroup.add(handlePlate);
    handleGroup.add(handleBar);
    handleGroup.position.set(19.84, 1.1, 0.5);
    this.add(handleGroup);

    // === WINDOW in the door ===
    // Window frame (beveled)
    const winFrame = new THREE.Mesh(new THREE.BoxGeometry(0.025, 0.48, 0.48), frameMat);
    winFrame.position.set(19.9, 1.7, 0);
    this.add(winFrame);

    // Glass pane (realistic)
    const glassMat2 = new THREE.MeshStandardMaterial({
      color: 0x88aacc,
      transparent: true,
      opacity: 0.25,
      roughness: 0.02,
      metalness: 0.1,
    });
    const glass = new THREE.Mesh(new THREE.PlaneGeometry(0.4, 0.4), glassMat2);
    glass.position.set(19.88, 1.7, 0);
    glass.rotation.y = Math.PI / 2;
    this.add(glass);

    // Wire mesh on window
    const wireMat = this.M(0x444448, 0.5, 0.7);
    for (let i = -3; i <= 3; i++) {
      const hWire = new THREE.Mesh(new THREE.BoxGeometry(0.004, 0.004, 0.38), wireMat);
      hWire.position.set(19.87, 1.7 + i * 0.055, 0);
      this.add(hWire);
      const vWire = new THREE.Mesh(new THREE.BoxGeometry(0.004, 0.38, 0.004), wireMat);
      vWire.position.set(19.87, 1.7, i * 0.055);
      this.add(vWire);
    }

    // === ZOMBIE behind the door ===
    this.addDoorZombie(20.8, 0, 0);

    // Room interior light (dark, eerie)
    const roomLight = new THREE.PointLight(0xff1500, 3, 12);
    roomLight.position.set(22, 3, 0);
    this.add(roomLight);

    // Light to illuminate zombie face at window
    const faceLight = new THREE.PointLight(0xffeedd, 4, 6);
    faceLight.position.set(20.3, 1.7, 0);
    this.add(faceLight);

    // Sparking electricity in the room
    for (let i = 0; i < 3; i++) {
      const sparkLight = new THREE.PointLight(0x4488ff, 4, 6);
      sparkLight.position.set(22 + i * 0.5, 2 + Math.random(), (Math.random() - 0.5) * 2);
      sparkLight.userData.isSpark = true;
      this.add(sparkLight);
    }
    for (let i = 0; i < 2; i++) {
      const sparkMesh = new THREE.Mesh(
        new THREE.SphereGeometry(0.06, 8, 6),
        new THREE.MeshBasicMaterial({ color: 0x88ccff })
      );
      sparkMesh.position.set(22 + i, 2.5, (Math.random() - 0.5) * 2);
      sparkMesh.userData.isSpark = true;
      this.add(sparkMesh);
    }

    // "DANGER" sign with border
    const signGroup = new THREE.Group();
    const signBg = new THREE.Mesh(
      new THREE.BoxGeometry(0.012, 0.14, 0.32),
      new THREE.MeshStandardMaterial({
        color: 0xdd0000, emissive: 0xdd0000, emissiveIntensity: 0.4,
      })
    );
    const signBorder = new THREE.Mesh(
      new THREE.BoxGeometry(0.013, 0.16, 0.34),
      this.M(0xffcc00, 0.4, 0.5)
    );
    signBorder.position.x = 0.001;
    signGroup.add(signBorder);
    signGroup.add(signBg);
    signGroup.position.set(19.84, 2.1, 0);
    this.add(signGroup);

    // Dead researchers behind player
    this.addDeadResearcher(-6, 1.0, 'back', 1.5);
    this.addDeadResearcher(-8, -0.5, 'face_down', 0.8);

    // Water puddles (adds realism)
    const puddleMat = new THREE.MeshStandardMaterial({
      color: 0x111115, roughness: 0.02, metalness: 0.0,
    });
    for (const [px, pz] of [[-12, 1.5], [7, -2], [-1, 3], [14, -0.5]] as [number, number][]) {
      const puddle = new THREE.Mesh(
        new THREE.CircleGeometry(0.2 + Math.random() * 0.4, 16),
        puddleMat
      );
      puddle.rotation.x = -Math.PI / 2;
      puddle.position.set(px, 0.006, pz);
      this.add(puddle);
    }
  }

  // ==================== ESCAPE SCENE ====================
  private buildEscapeScene(): void {
    this.scene.fog = new THREE.FogExp2(0x8899aa, 0.003);

    // Strong ambient so corridor is visible
    this.scene.add(new THREE.AmbientLight(0x667788, 2.0));
    this.objects.push(this.scene.children[this.scene.children.length - 1]);

    // Hemisphere fill (floor bounce light)
    const hemi = new THREE.HemisphereLight(0x556677, 0x221111, 0.8);
    this.add(hemi);

    // Floor
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(70, 6), this.wetFloorMat());
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.add(floor);

    // Ceiling
    const ceil = new THREE.Mesh(new THREE.PlaneGeometry(70, 6), this.M(0x2a2a2a));
    ceil.rotation.x = Math.PI / 2; ceil.position.y = 3.5;
    this.add(ceil);

    for (const z of [-3, 3]) {
      const wall = new THREE.Mesh(new THREE.BoxGeometry(70, 3.5, 0.15), this.M(0x454545));
      wall.position.set(5, 1.75, z);
      this.add(wall);
    }

    // Emergency red lights + white corridor lights
    for (let x = -25; x <= 30; x += 4) {
      // Red emergency
      const pl = new THREE.PointLight(0xff3300, 4, 12);
      pl.position.set(x, 3.2, 0);
      pl.userData.isEmergency = true;
      this.add(pl);

      const fix = new THREE.Mesh(
        new THREE.CylinderGeometry(0.05, 0.05, 0.04, 8),
        new THREE.MeshStandardMaterial({ color: 0xff0000, emissive: 0xff0000, emissiveIntensity: 2 })
      );
      fix.position.set(x, 3.35, 0);
      this.add(fix);
    }

    // White corridor lights (so walls/floor/dead zombies are visible)
    for (let x = -20; x <= 25; x += 8) {
      const wl = new THREE.PointLight(0xddeeff, 2.5, 14);
      wl.position.set(x, 3.3, 0);
      this.add(wl);
    }

    // Dead zombies on ground
    const deadMat = this.skinMat(0x3a4a2a);
    for (let x = -18; x <= 20; x += 4) {
      const g = new THREE.Group();
      const body = new THREE.Mesh(new THREE.BoxGeometry(0.5, 1.4, 0.3), deadMat);
      g.add(body);
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.15, 12, 10), deadMat);
      head.position.set(0, 0.8, 0);
      g.add(head);
      g.position.set(x + Math.random() * 2, 0.15, (Math.random() - 0.5) * 4);
      g.rotation.x = Math.PI / 2;
      g.rotation.z = Math.random() * Math.PI;
      g.castShadow = false;
      this.add(g);

      // Blood pool
      const poolMat = new THREE.MeshStandardMaterial({
        color: 0x550000, roughness: 0.3,
      });
      const pool = new THREE.Mesh(new THREE.CircleGeometry(0.4, 12), poolMat);
      pool.rotation.x = -Math.PI / 2;
      pool.position.set(g.position.x, 0.003, g.position.z);
      this.add(pool);
    }

    // === EMERGENCY EXIT DOOR at end of corridor (ground level) ===
    const frameMat = this.M(0x666666, 0.4, 0.6);
    const doorX = 30;
    // Door frame
    const frameL = new THREE.Mesh(new THREE.BoxGeometry(0.12, 2.8, 0.15), frameMat);
    frameL.position.set(doorX, 1.4, -1.05);
    this.add(frameL);
    const frameR = new THREE.Mesh(new THREE.BoxGeometry(0.12, 2.8, 0.15), frameMat);
    frameR.position.set(doorX, 1.4, 1.05);
    this.add(frameR);
    const frameT = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.15, 2.25), frameMat);
    frameT.position.set(doorX, 2.85, 0);
    this.add(frameT);

    // Emergency door (metal, with push bar)
    const exitDoorMat = this.M(0x556655, 0.4, 0.6);
    const exitDoor = new THREE.Mesh(new THREE.BoxGeometry(0.1, 2.6, 2.0), exitDoorMat);
    exitDoor.position.set(doorX, 1.3, 0);
    exitDoor.userData.isExitDoor = true;
    this.add(exitDoor);
    // Push bar on door
    const pushBar = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.06, 1.4), this.M(0x888888, 0.3, 0.8));
    pushBar.position.set(doorX - 0.08, 1.1, 0);
    this.add(pushBar);

    // "EXIT" sign above door (glowing green)
    const exitSign = new THREE.Mesh(
      new THREE.BoxGeometry(0.05, 0.15, 0.6),
      new THREE.MeshStandardMaterial({ color: 0x00ff00, emissive: 0x00ff00, emissiveIntensity: 2 })
    );
    exitSign.position.set(doorX - 0.05, 3.0, 0);
    this.add(exitSign);

    // Wall around door
    const wallAroundDoor = this.M(0x454545);
    const wallLeft = new THREE.Mesh(new THREE.BoxGeometry(0.3, 3.5, 3), wallAroundDoor);
    wallLeft.position.set(doorX, 1.75, -2.5);
    this.add(wallLeft);
    const wallRight = new THREE.Mesh(new THREE.BoxGeometry(0.3, 3.5, 3), wallAroundDoor);
    wallRight.position.set(doorX, 1.75, 2.5);
    this.add(wallRight);
    const wallTop = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.5, 8), wallAroundDoor);
    wallTop.position.set(doorX, 3.25, 0);
    this.add(wallTop);

    // === OUTSIDE WORLD ===
    const sun = new THREE.DirectionalLight(0xffffdd, 8);
    sun.position.set(40, 10, 3);
    this.add(sun);
    const sun2 = new THREE.PointLight(0xffeecc, 15, 50);
    sun2.position.set(34, 6, 0);
    this.add(sun2);
    const fillOut = new THREE.PointLight(0xffffff, 8, 30);
    fillOut.position.set(33, 3, 0);
    this.add(fillOut);
    const exitSpill = new THREE.PointLight(0xffeedd, 6, 25);
    exitSpill.position.set(28, 2, 0);
    this.add(exitSpill);

    // Sky
    const skyMat = new THREE.MeshBasicMaterial({ color: 0x6699cc, side: THREE.BackSide });
    const skyBox = new THREE.Mesh(new THREE.SphereGeometry(60, 16, 12), skyMat);
    skyBox.position.set(40, 5, 0);
    this.add(skyBox);

    // Ground outside
    const groundOut = new THREE.Mesh(new THREE.PlaneGeometry(40, 40), this.M(0x555555, 0.8));
    groundOut.rotation.x = -Math.PI / 2;
    groundOut.position.set(45, 0, 0);
    this.add(groundOut);

    // City buildings (closer, visible, lit)
    const buildingColors = [0x556677, 0x445566, 0x667788, 0x4a5a6a, 0x5a6a7a];
    for (let i = 0; i < 10; i++) {
      const bh = 3 + Math.random() * 8;
      const bw = 1.5 + Math.random() * 2;
      const color = buildingColors[Math.floor(Math.random() * buildingColors.length)];
      const bMat = this.M(color, 0.7, 0.1);
      const building = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, bw), bMat);
      building.position.set(38 + i * 3 + Math.random() * 2, bh / 2, -8 + Math.random() * 16);
      this.add(building);

      // Windows (lit, yellow rectangles on front face)
      for (let wy = 1.5; wy < bh - 0.5; wy += 1.2) {
        for (let wz = -bw * 0.3; wz <= bw * 0.3; wz += 0.8) {
          if (Math.random() > 0.3) {
            const isLit = Math.random() > 0.4;
            const win = new THREE.Mesh(
              new THREE.PlaneGeometry(0.4, 0.6),
              new THREE.MeshBasicMaterial({
                color: isLit ? 0xffdd88 : 0x334455,
              })
            );
            win.position.set(building.position.x - bw / 2 - 0.01, wy, building.position.z + wz);
            win.rotation.y = Math.PI / 2;
            this.add(win);
          }
        }
      }
    }

    // Road
    const road = new THREE.Mesh(
      new THREE.PlaneGeometry(30, 6),
      this.M(0x333333, 0.85)
    );
    road.rotation.x = -Math.PI / 2;
    road.position.set(50, 0.01, 0);
    this.add(road);
    // Road center line
    for (let rx = 36; rx < 65; rx += 3) {
      const line = new THREE.Mesh(
        new THREE.PlaneGeometry(1.5, 0.12),
        this.M(0xcccc66, 0.6)
      );
      line.rotation.x = -Math.PI / 2;
      line.position.set(rx, 0.02, 0);
      this.add(line);
    }

    // Trees (simple cylinder trunk + sphere canopy)
    for (let i = 0; i < 4; i++) {
      const tx = 36 + i * 5;
      const tz = (i % 2 === 0 ? -4 : 4) + Math.random() * 2;
      const trunk = new THREE.Mesh(
        new THREE.CylinderGeometry(0.12, 0.15, 2, 6),
        this.M(0x5a3a1a, 0.8)
      );
      trunk.position.set(tx, 1, tz);
      this.add(trunk);
      const canopy = new THREE.Mesh(
        new THREE.SphereGeometry(1.2, 8, 6),
        this.M(0x2a5a2a, 0.8)
      );
      canopy.position.set(tx, 2.8, tz);
      this.add(canopy);
    }

    // Clouds
    for (let i = 0; i < 5; i++) {
      const cloud = new THREE.Mesh(
        new THREE.SphereGeometry(2 + Math.random() * 2, 8, 6),
        new THREE.MeshBasicMaterial({ color: 0xddddee })
      );
      cloud.scale.set(2, 0.5, 1);
      cloud.position.set(45 + Math.random() * 30, 15 + Math.random() * 5, -10 + Math.random() * 20);
      this.add(cloud);
    }

    // === ZOMBIES IN THE CITY ===
    const zombieSkinMat = this.skinMat(0x4a5a3a);
    const zombieClothMat = this.M(0x3a3a3a, 0.8);

    // Walking zombies on the street
    const zombiePositions = [
      { x: 40, z: -2, ry: 0.3 },
      { x: 44, z: 3, ry: -0.5 },
      { x: 48, z: -1, ry: 1.2 },
      { x: 38, z: 5, ry: 2.0 },
      { x: 52, z: 0, ry: -1.0 },
    ];
    for (const zp of zombiePositions) {
      const zg = new THREE.Group();
      // Body
      const zbody = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.6, 0.2), zombieClothMat);
      zbody.position.y = 1.1;
      zg.add(zbody);
      // Head
      const zhead = new THREE.Mesh(new THREE.SphereGeometry(0.14, 8, 6), zombieSkinMat);
      zhead.position.y = 1.55;
      zg.add(zhead);
      // Arms
      for (const side of [-1, 1]) {
        const arm = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.4, 0.08), zombieSkinMat);
        arm.position.set(side * 0.22, 1.0, -0.05);
        arm.rotation.x = -0.4;
        zg.add(arm);
      }
      // Legs
      for (const side of [-1, 1]) {
        const leg = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.5, 0.1), zombieClothMat);
        leg.position.set(side * 0.1, 0.45, 0);
        zg.add(leg);
      }
      zg.position.set(zp.x, 0, zp.z);
      zg.rotation.y = zp.ry;
      zg.userData.isWalkingZombie = true;
      this.add(zg);
    }

    // Zombies coming out of buildings
    const doorZombies = [
      { x: 39, z: -6, ry: -Math.PI / 2 },
      { x: 45, z: 6, ry: Math.PI / 2 },
    ];
    for (const dz of doorZombies) {
      const dzg = new THREE.Group();
      const dzbody = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.6, 0.2), this.skinMat(0x5a4a3a));
      dzbody.position.y = 1.1;
      dzg.add(dzbody);
      const dzhead = new THREE.Mesh(new THREE.SphereGeometry(0.14, 8, 6), this.skinMat(0x5a4a3a));
      dzhead.position.y = 1.55;
      dzg.add(dzhead);
      for (const side of [-1, 1]) {
        const arm = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.4, 0.08), this.skinMat(0x5a4a3a));
        arm.position.set(side * 0.22, 1.0, -0.1);
        arm.rotation.x = -0.6;
        dzg.add(arm);
      }
      dzg.position.set(dz.x, 0, dz.z);
      dzg.rotation.y = dz.ry;
      dzg.userData.isWalkingZombie = true;
      this.add(dzg);
    }

    this.addDustParticles();
  }

  // ==================== KEYFRAMES ====================
  private getIntroKeyframes(): CutsceneKeyframe[] {
    const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
    return [
      { time: 0, text: '', camPos: v(-25, 1.6, 0), lookAt: v(-20, 1.4, 0) },
      { time: 2, text: '2087年 — ゼノバイオテック研究所', camPos: v(-20, 1.6, 0), lookAt: v(-10, 1.3, 0) },
      { time: 5, text: '極秘のウイルス研究が行われていた施設で\n原因不明の事故が発生した', camPos: v(-8, 1.7, 0.5), lookAt: v(0, 1.0, -1) },
      { time: 9, text: '血だまり...倒れている研究員たち...', camPos: v(-2, 1.4, -0.5), lookAt: v(3, 0.3, 0.5) },
      { time: 11, text: 'ここで一体何が起きたんだ...', camPos: v(3, 1.6, 0), lookAt: v(14, 1.5, 0) },
      { time: 14, text: '', camPos: v(8, 1.6, 0), lookAt: v(18, 1.5, 0.5) },
      { time: 16, text: '廊下の奥に...厳重な扉がある', camPos: v(12, 1.6, 0), lookAt: v(20, 1.7, 0) },
      { time: 19, text: '', camPos: v(17, 1.7, 0), lookAt: v(20, 1.7, 0),
        action: () => { audioManager.playHeartbeat(4); } },
      { time: 21, text: '', camPos: v(19.3, 1.7, 0), lookAt: v(22, 1.7, 0) },
      { time: 23, text: '...中は暗くて...何も...', camPos: v(19.4, 1.7, 0), lookAt: v(22, 1.6, 0) },
      { time: 24, text: '', camPos: v(19.4, 1.7, 0), lookAt: v(20.2, 1.7, 0),
        action: () => { audioManager.playGlassBreak(); audioManager.playZombieGrowl(); audioManager.playTensionRise(); } },
      { time: 25, text: '...!!', camPos: v(17, 1.7, 0), lookAt: v(20, 1.8, 0) },
      { time: 26, text: '', camPos: v(15, 1.7, 0.3), lookAt: v(20, 1.7, 0),
        action: () => { audioManager.playDoorSmash(); } },
      // Zombie charges toward player
      { time: 27, text: '', camPos: v(14, 1.7, 0), lookAt: v(18, 1.7, 0),
        action: () => { audioManager.playZombieGrowl(); } },
      // Zombie reaches player — left hand GRABS
      { time: 28.5, text: '', camPos: v(13, 1.7, 0), lookAt: v(12.5, 1.7, 0) },
      // Held — staring at zombie face close up
      { time: 29.5, text: '', camPos: v(12.8, 1.7, 0), lookAt: v(12.3, 1.8, 0),
        action: () => { audioManager.playZombieGrowl(); } },
      // Right fist PUNCHES — head snaps
      { time: 30.0, text: '', camPos: v(12.6, 1.65, 0.2), lookAt: v(13, 2, 0.3), roll: 0.05,
        action: () => { audioManager.playPunch(); } },
      // Impact — start falling smoothly
      { time: 30.6, text: '', camPos: v(12.5, 1.4, 0.35), lookAt: v(12.8, 1.6, 0.8), roll: 0.15 },
      // Knees giving way
      { time: 31.2, text: '', camPos: v(12.4, 1.0, 0.5), lookAt: v(12.6, 1.1, 1.2), roll: 0.35 },
      // Falling sideways
      { time: 31.8, text: '', camPos: v(12.35, 0.6, 0.65), lookAt: v(12.5, 0.7, 1.6), roll: 0.65 },
      // Approaching floor
      { time: 32.4, text: '', camPos: v(12.3, 0.3, 0.8), lookAt: v(12.4, 0.3, 2.0), roll: 1.0,
        action: () => { audioManager.playBodyFall(); } },
      // Head on floor
      { time: 33.0, text: '意識が...遠のいていく...', camPos: v(12.25, 0.12, 0.85), lookAt: v(12.3, 0.1, 2.5), roll: Math.PI / 2,
        action: () => { audioManager.playHeartbeat(3); } },
      // Fade out
      { time: 35, text: '', camPos: v(12.25, 0.1, 0.85), lookAt: v(12.3, 0.08, 2.5), roll: Math.PI / 2 },
    ];
  }

  private getEscapeKeyframes(): CutsceneKeyframe[] {
    const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
    return [
      // Walking through corridor
      { time: 0, text: '', camPos: v(-22, 1.5, 0), lookAt: v(-15, 1.3, 0) },
      { time: 2, text: '研究所の全てのゾンビを殲滅した', camPos: v(-15, 1.5, 0), lookAt: v(-5, 1.2, 0) },
      { time: 5, text: '施設の緊急ロックが解除される', camPos: v(-5, 1.6, 0), lookAt: v(10, 1.4, 0) },
      { time: 8, text: '脱出口を探す...', camPos: v(8, 1.6, 0), lookAt: v(22, 1.5, 0) },
      // See emergency door ahead
      { time: 11, text: '非常ドアが見える...!', camPos: v(22, 1.6, 0), lookAt: v(30, 1.5, 0) },
      // Approaching the door
      { time: 13, text: '', camPos: v(26, 1.6, 0), lookAt: v(30, 1.5, 0) },
      // Door opens — light floods in
      { time: 14, text: '扉を開ける...', camPos: v(28, 1.6, 0), lookAt: v(30, 1.5, 0) },
      // Stepping through — outside world
      { time: 16, text: '外の光が目に飛び込んでくる', camPos: v(31, 1.6, 0), lookAt: v(40, 1.5, 0) },
      // Standing outside — see the city
      { time: 18, text: '長い悪夢がようやく終わった...', camPos: v(33, 1.7, 0.5), lookAt: v(45, 1.5, 0) },
      // Notice zombies on the street
      { time: 21, text: '...しかし', camPos: v(34, 1.7, -0.5), lookAt: v(40, 1.3, -2) },
      { time: 23, text: '街にもゾンビが...歩いている...', camPos: v(34, 1.7, 1), lookAt: v(44, 1.3, 3) },
      // See zombie coming out of building
      { time: 25, text: '建物からも次々と...', camPos: v(34, 2, -1), lookAt: v(39, 1.3, -6) },
      // Pan wide — city is overrun
      { time: 27, text: 'ステージ1 クリア\n— 戦いはまだ終わらない —', camPos: v(34, 3, 0), lookAt: v(50, 2, 0) },
      { time: 30, text: '', camPos: v(34, 4, 0), lookAt: v(50, 3, 0) },
    ];
  }

  // ==================== PLAYBACK ====================
  private runKeyframes(keyframes: CutsceneKeyframe[]): Promise<void> {
    return new Promise((resolve) => {
      const container = document.createElement('div');
      container.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;z-index:50;pointer-events:auto;';
      container.innerHTML = `
        <div style="position:absolute;top:0;left:0;width:100%;height:6%;background:linear-gradient(180deg,rgba(0,0,0,0.6),transparent);pointer-events:none;"></div>
        <div style="position:absolute;bottom:0;left:0;width:100%;height:18%;background:linear-gradient(0deg,rgba(0,0,0,0.7),transparent);pointer-events:none;"></div>
        <div id="cs-text" style="position:absolute;bottom:8%;left:50%;transform:translateX(-50%);
          color:#eee;font-size:1.3rem;text-align:center;line-height:2;white-space:pre-line;
          opacity:0;max-width:700px;font-family:serif;letter-spacing:0.06em;
          text-shadow:0 2px 15px rgba(0,0,0,1),0 0 30px rgba(0,0,0,0.9);
          pointer-events:none;"></div>
        <div style="position:absolute;bottom:12px;right:16px;color:rgba(255,255,255,0.25);font-size:0.7rem;pointer-events:none;">
          Click / Space to skip</div>
      `;
      // Cinematic letterbox bars
      const topBar = document.createElement('div');
      topBar.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:8%;background:#000;pointer-events:none;';
      container.appendChild(topBar);
      const bottomBar = document.createElement('div');
      bottomBar.style.cssText = 'position:absolute;bottom:0;left:0;width:100%;height:8%;background:#000;pointer-events:none;';
      container.appendChild(bottomBar);

      this.overlay.appendChild(container);

      let isPlaying = true;
      let elapsed = 0;
      const totalDuration = keyframes[keyframes.length - 1].time;

      const finish = () => {
        if (!isPlaying) return;
        isPlaying = false;
        container.remove();
        resolve();
      };

      container.addEventListener('click', finish);
      const keyHandler = (e: KeyboardEvent) => {
        if (e.code === 'Space' || e.code === 'Escape') {
          window.removeEventListener('keydown', keyHandler);
          finish();
        }
      };
      window.addEventListener('keydown', keyHandler);

      const textEl = container.querySelector('#cs-text') as HTMLElement;
      let lastTime = performance.now();
      let animTime = 0;

      const animate = () => {
        if (!isPlaying) return;
        const now = performance.now();
        const dt = (now - lastTime) / 1000;
        lastTime = now;
        elapsed += dt;
        animTime += dt;

        if (elapsed >= totalDuration) { finish(); return; }

        // Find current segment
        let kfA = keyframes[0];
        let kfB = keyframes[1];
        for (let i = 0; i < keyframes.length - 1; i++) {
          if (elapsed >= keyframes[i].time && elapsed < keyframes[i + 1].time) {
            kfA = keyframes[i];
            kfB = keyframes[i + 1];
            break;
          }
        }

        // Smooth interpolation (cubic ease)
        const segDuration = kfB.time - kfA.time;
        const segProgress = Math.max(0, Math.min(1, (elapsed - kfA.time) / segDuration));
        const ease = segProgress < 0.5
          ? 4 * segProgress * segProgress * segProgress
          : 1 - Math.pow(-2 * segProgress + 2, 3) / 2;

        // Camera position
        this.camera.position.lerpVectors(kfA.camPos, kfB.camPos, ease);

        // Camera shake
        if (this.cameraShake.intensity > 0.001) {
          this.camera.position.x += (Math.random() - 0.5) * this.cameraShake.intensity;
          this.camera.position.y += (Math.random() - 0.5) * this.cameraShake.intensity * 0.7;
          this.camera.position.z += (Math.random() - 0.5) * this.cameraShake.intensity * 0.5;
          this.cameraShake.intensity *= this.cameraShake.decay;
        }

        // Look at
        const lookTarget = new THREE.Vector3().lerpVectors(kfA.lookAt, kfB.lookAt, ease);
        this.camera.lookAt(lookTarget);

        // Camera roll (tilt) interpolation
        const rollA = kfA.roll ?? 0;
        const rollB = kfB.roll ?? 0;
        if (rollA !== 0 || rollB !== 0) {
          const roll = rollA + (rollB - rollA) * ease;
          this.camera.rotateZ(roll);
        }

        // Text
        let currentText = '';
        for (let i = keyframes.length - 1; i >= 0; i--) {
          if (elapsed >= keyframes[i].time) {
            currentText = keyframes[i].text;
            break;
          }
        }

        if (currentText) {
          textEl.textContent = currentText;
          const textStart = kfA.time;
          const textEnd = kfB.time;
          const textDur = textEnd - textStart;
          const textProgress = (elapsed - textStart) / textDur;
          if (textProgress < 0.1) {
            textEl.style.opacity = String(textProgress / 0.1);
          } else if (textProgress > 0.9) {
            textEl.style.opacity = String((1 - textProgress) / 0.1);
          } else {
            textEl.style.opacity = '1';
          }
        } else {
          textEl.style.opacity = '0';
        }

        // Run actions
        if (kfA.action) {
          kfA.action({ scene: this.scene, objects: this.objects, camera: this.camera });
          kfA.action = undefined;
        }

        // Footstep sounds while camera is moving (walking phases, not falling)
        if (elapsed < 23 && elapsed > 0.5) {
          const speed = kfA.camPos.distanceTo(kfB.camPos) / segDuration;
          if (speed > 0.5 && elapsed - this.lastFootstepTime > 0.45) {
            audioManager.playFootstep();
            this.lastFootstepTime = elapsed;
          }
        }

        // Animate scene
        this.animateScene(animTime, elapsed);

        // Animate dust
        this.animateDust(animTime);

        // Render via post-processing
        this.composer.render();
        requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
    });
  }

  private doorSmashed = false;
  private zombieGrabbing = false;

  private animateScene(t: number, elapsed: number): void {

    for (const obj of this.objects) {
      if (!obj.userData.isDoorZombie) continue;

      // Dynamically face toward camera (face built at -Z, use atan2 + PI)
      const dx = this.camera.position.x - obj.position.x;
      const dz = this.camera.position.z - obj.position.z;
      const faceAngle = Math.atan2(dx, dz) + Math.PI;

      if (elapsed < 23.5) {
        // === Phase 1: Zombie lurking in dark room ===
        obj.position.set(20.8, 0, 0);
        obj.rotation.set(0, faceAngle + Math.sin(t * 0.5) * 0.03, 0);
        obj.position.y = Math.sin(t * 1.5) * 0.005;
      } else if (elapsed < 25) {
        // === Phase 2: Zombie lunges to window ===
        const p = Math.min(1, (elapsed - 23.5) / 0.3);
        obj.position.x = 20.8 - p * 0.7;
        obj.position.z = 0;
        obj.rotation.set(0, faceAngle, 0);

        // Camera shake on slam
        if (p > 0.8 && this.cameraShake.intensity < 0.01) {
          this.cameraShake.intensity = 0.06;
        }
      } else if (elapsed < 26.5) {
        // === Phase 3: Staring through window, scratching ===
        obj.position.set(20.1, 0, 0);
        obj.rotation.set(0, faceAngle, Math.sin(t * 2) * 0.05);
        obj.position.y = Math.sin(t * 3) * 0.015;
        // Arms reaching/scratching at window
        obj.children.forEach(child => {
          if (child.userData.isLeftArm || child.userData.isRightArm) {
            child.rotation.x = -0.8 + Math.sin(t * 4 + (child.userData.isLeftArm ? 0 : 1.5)) * 0.3;
          }
        });
      } else if (elapsed < 27) {
        // === Phase 4: Zombie breaks through door ===
        if (!this.doorSmashed) {
          this.doorSmashed = true;
          this.cameraShake.intensity = 0.12;
          for (const o of this.objects) {
            if (o instanceof THREE.Mesh && o.geometry instanceof THREE.BoxGeometry) {
              const p = o.geometry.parameters;
              if (p && Math.abs(p.width - 0.15) < 0.02 && Math.abs(p.height - 2.5) < 0.1) {
                o.position.x -= 3;
                o.rotation.y = 1.8;
                o.rotation.z = 0.6;
                o.position.y = 0.2;
              }
            }
          }
        }
        const p = (elapsed - 26.5) / 0.5;
        obj.position.x = 20.1 - p * 2;
        obj.rotation.set(0, faceAngle, 0);
        // Shambling through doorway
        obj.children.forEach(child => {
          if (child.userData.isLeg) {
            child.rotation.x = Math.sin(t * 6) * 0.35;
          }
          if (child.userData.isLeftArm || child.userData.isRightArm) {
            child.rotation.x = -0.7;
          }
        });
      } else {
        // === Phase 5: Zombie rushes → left hand GRABS → right hand PUNCHES ===
        // Timeline: 27-28.5 rush, 28.5 grab, 29.5-30 wind up + punch
        this.zombieGrabbing = true;
        const phaseTime = elapsed - 27;

        if (phaseTime < 1.5) {
          // --- RUSH toward player ---
          const rushP = phaseTime / 1.5;
          obj.position.x = 18 - rushP * 6;
          obj.position.z = 0;
          obj.rotation.set(-rushP * 0.1, faceAngle, 0);

          obj.children.forEach(child => {
            if (child.userData.isLeg) {
              child.rotation.x = Math.sin(t * 10) * 0.5;
            }
            // Left arm reaching forward
            if (child.userData.isLeftArm) {
              child.rotation.x = -0.5 - rushP * 1.0;
              child.rotation.z = rushP * 0.3;
            }
            // Right arm loose running swing
            if (child.userData.isRightArm) {
              child.rotation.x = -0.3 + Math.sin(t * 8) * 0.3;
              child.rotation.z = 0;
            }
          });
          obj.position.y = Math.abs(Math.sin(t * 8)) * 0.04;

        } else if (phaseTime < 2.5) {
          // --- LEFT HAND GRABS player (28.5-29.5) ---
          obj.position.x = 12;
          obj.position.y = 0;
          obj.rotation.set(-0.1, faceAngle, 0);

          // Camera shake on grab
          if (phaseTime < 1.55 && this.cameraShake.intensity < 0.01) {
            this.cameraShake.intensity = 0.1;
          }

          obj.children.forEach(child => {
            if (child.userData.isLeg) {
              child.rotation.x = 0; // standing still
            }
            // Left arm locked: holding player
            if (child.userData.isLeftArm) {
              child.rotation.x = -1.5;
              child.rotation.z = 0.4 + Math.sin(t * 8) * 0.04;
            }
            // Right arm at side, preparing
            if (child.userData.isRightArm) {
              child.rotation.x = -0.2;
              child.rotation.z = 0;
            }
            if (child.userData.isHead) {
              child.rotation.x = -0.15;
            }
          });

        } else if (phaseTime < 3.0) {
          // --- WIND UP right fist (29.5-30.0) ---
          const windP = (phaseTime - 2.5) / 0.5;
          obj.position.x = 12;
          obj.position.y = 0;
          obj.rotation.set(-0.1, faceAngle, 0);

          obj.children.forEach(child => {
            if (child.userData.isLeg) child.rotation.x = 0;
            if (child.userData.isLeftArm) {
              child.rotation.x = -1.5;
              child.rotation.z = 0.4;
            }
            // Right arm winds back
            if (child.userData.isRightArm) {
              child.rotation.x = -0.2 + windP * 1.5; // arm goes way back
              child.rotation.z = -windP * 0.4;
            }
            if (child.userData.isHead) {
              child.rotation.x = -0.15 - windP * 0.1;
            }
          });

        } else {
          // --- PUNCH lands (30.0+) ---
          const punchP = Math.min(1, (phaseTime - 3.0) / 0.2);
          const ease = 1 - Math.pow(1 - punchP, 3);
          obj.position.x = 12;
          obj.position.y = 0;
          obj.rotation.set(-0.1, faceAngle, 0);

          // Big camera shake on punch
          if (punchP < 0.15 && this.cameraShake.intensity < 0.01) {
            this.cameraShake.intensity = 0.3;
          }

          obj.children.forEach(child => {
            if (child.userData.isLeg) child.rotation.x = 0;
            if (child.userData.isLeftArm) {
              child.rotation.x = -1.5;
              child.rotation.z = 0.4;
            }
            // Right arm swings forward with full force
            if (child.userData.isRightArm) {
              child.rotation.x = 1.3 - ease * 2.8;
              child.rotation.z = -0.4 + ease * 0.3;
            }
            if (child.userData.isHead) {
              child.rotation.x = -0.25 - ease * 0.2;
            }
          });
        }
      }
    }

    for (const obj of this.objects) {
      // Zombie sway (background zombies only — not door zombie)
      if (obj.userData.isZombie && !obj.userData.isDoorZombie) {
        obj.rotation.y += Math.sin(t * 0.7 + obj.position.x) * 0.003;
        obj.children.forEach((child, i) => {
          if (i < 2) return;
          if (child.position.y > 0.8) {
            child.rotation.x = -0.5 + Math.sin(t * 1.2 + i) * 0.2;
          }
        });
        obj.position.y = Math.abs(Math.sin(t * 0.8 + obj.position.x)) * 0.03;
      }
      // Emergency light flicker
      if (obj instanceof THREE.PointLight && obj.userData.isEmergency) {
        obj.intensity = 2 + Math.sin(t * 8 + obj.position.x) * 1 + Math.random() * 0.3;
      }
      // Fluorescent flicker
      if (obj instanceof THREE.PointLight && obj.userData.isFlicker) {
        const base = 3;
        obj.intensity = Math.random() > 0.08 ? base : 0;
      }
      // Electrical sparks
      if (obj instanceof THREE.PointLight && obj.userData.isSpark) {
        obj.intensity = Math.random() > 0.6 ? 4 + Math.random() * 6 : 0.1;
      }
      if (obj instanceof THREE.Mesh && obj.userData.isSpark) {
        obj.visible = Math.random() > 0.5;
        obj.scale.setScalar(0.5 + Math.random() * 1.5);
      }

      // Exit door opening animation (escape scene, around t=13.5-15)
      if (obj instanceof THREE.Mesh && obj.userData.isExitDoor) {
        if (elapsed > 13.5 && elapsed < 15.5) {
          const doorP = Math.min(1, (elapsed - 13.5) / 1.5);
          const ease = 1 - Math.pow(1 - doorP, 3);
          obj.rotation.y = ease * Math.PI / 2;
          obj.position.z = -1 + Math.sin(obj.rotation.y) * 1;
          obj.position.x = 30 - (1 - Math.cos(obj.rotation.y)) * 0.5;
        } else if (elapsed >= 15.5) {
          obj.rotation.y = Math.PI / 2;
        }
      }

      // Walking zombies shuffle animation
      if (obj.userData.isWalkingZombie) {
        const bobPhase = elapsed * 2 + obj.position.x;
        obj.position.x += Math.sin(bobPhase) * 0.002;
        obj.position.z += Math.cos(bobPhase * 0.7) * 0.001;
        // Slight body sway
        obj.rotation.z = Math.sin(bobPhase) * 0.03;
      }
    }
  }

  // ==================== PARTICLES ====================
  private addDustParticles(): void {
    const count = 80;
    const positions = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = -25 + Math.random() * 50;
      positions[i * 3 + 1] = Math.random() * 3.5;
      positions[i * 3 + 2] = -3.5 + Math.random() * 7;
      sizes[i] = 0.01 + Math.random() * 0.02;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const mat = new THREE.PointsMaterial({
      color: 0xaaaaaa,
      size: 0.02,
      transparent: true,
      opacity: 0.3,
      sizeAttenuation: true,
      depthWrite: false,
    });

    this.dustParticles = new THREE.Points(geo, mat);
    this.add(this.dustParticles);
  }

  private animateDust(t: number): void {
    if (!this.dustParticles) return;
    const pos = this.dustParticles.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const ix = i * 3;
      // Gentle drift
      pos.array[ix] += Math.sin(t * 0.3 + i * 0.1) * 0.0008;
      pos.array[ix + 1] += Math.sin(t * 0.5 + i * 0.2) * 0.0003;
      pos.array[ix + 2] += Math.cos(t * 0.4 + i * 0.15) * 0.0005;
    }
    pos.needsUpdate = true;
  }

  // ==================== HELPERS ====================
  private add(obj: THREE.Object3D): void {
    this.scene.add(obj);
    this.objects.push(obj);
  }

  private makeDesk(x: number, z: number): THREE.Group {
    const g = new THREE.Group();
    const topMat = new THREE.MeshStandardMaterial({
      color: 0x3d4d5d, roughness: 0.55, metalness: 0.1,
    });
    const top = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.06, 0.75), topMat);
    top.position.y = 0.88;
    top.castShadow = false;
    top.receiveShadow = true;
    g.add(top);

    const legMat = this.M(0x555555, 0.35, 0.65);
    for (const [lx, lz] of [[-0.8, -0.3], [-0.8, 0.3], [0.8, -0.3], [0.8, 0.3]]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.88, 0.035), legMat);
      leg.position.set(lx, 0.44, lz);
      leg.castShadow = false;
      g.add(leg);
    }

    // Drawer panel
    const drawer = new THREE.Mesh(
      new THREE.BoxGeometry(0.8, 0.25, 0.7),
      this.M(0x3a4a5a, 0.6, 0.1)
    );
    drawer.position.set(0.4, 0.72, 0);
    g.add(drawer);
    // Drawer handle
    const dHandle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.008, 0.008, 0.12, 6),
      this.M(0x888888, 0.3, 0.8)
    );
    dHandle.rotation.z = Math.PI / 2;
    dHandle.position.set(0.4, 0.72, -0.36);
    g.add(dHandle);

    g.position.set(x, 0, z);
    return g;
  }

  private addChairOnSide(x: number, z: number): void {
    const chairGroup = new THREE.Group();
    const seatMat = this.M(0x444450, 0.65, 0.1);
    chairGroup.add(new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.06, 0.5), seatMat));
    const chairBack = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.45, 0.04), seatMat);
    chairBack.position.set(0, 0.22, 0.22);
    chairGroup.add(chairBack);

    const poleMat = this.M(0x888890, 0.25, 0.8);
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.35, 8), poleMat);
    pole.position.y = -0.2;
    chairGroup.add(pole);

    // Wheels
    for (const [wx, wz] of [[-0.15, -0.15], [0.15, -0.15], [-0.15, 0.15], [0.15, 0.15], [0, 0.18]]) {
      const wheel = new THREE.Mesh(new THREE.SphereGeometry(0.025, 8, 6), this.M(0x333333));
      wheel.position.set(wx, -0.38, wz);
      chairGroup.add(wheel);
    }

    chairGroup.position.set(x, 0.4, z);
    chairGroup.rotation.z = 1.3;
    chairGroup.rotation.y = 0.5;
    chairGroup.castShadow = false;
    this.add(chairGroup);
  }

  private addTippedCart(x: number, z: number): void {
    const cartGroup = new THREE.Group();
    const cartMat = this.M(0x888890, 0.35, 0.55);
    cartGroup.add(new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.5, 0.4), cartMat));

    for (const [wx, wz] of [[-0.3, -0.15], [0.3, -0.15], [-0.3, 0.15], [0.3, 0.15]] as [number, number][]) {
      const wh = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.03, 12), this.M(0x333333));
      wh.rotation.x = Math.PI / 2;
      wh.position.set(wx, -0.3, wz);
      cartGroup.add(wh);
    }

    cartGroup.position.set(x, 0.4, z);
    cartGroup.rotation.z = 0.8;
    cartGroup.castShadow = false;
    this.add(cartGroup);

    // Green toxic spill
    const spillMat = new THREE.MeshStandardMaterial({
      color: 0x22bb22, emissive: 0x11aa11, emissiveIntensity: 0.3,
    });
    const spill = new THREE.Mesh(new THREE.CircleGeometry(0.35, 12), spillMat);
    spill.rotation.x = -Math.PI / 2;
    spill.position.set(x + 0.5, 0.008, z + 0.5);
    this.add(spill);

    // Spill glow
    const spillGlow = new THREE.PointLight(0x22ff44, 0.5, 3);
    spillGlow.position.set(x + 0.5, 0.2, z + 0.5);
    this.add(spillGlow);
  }

  private addFlippedDesk(x: number, z: number): void {
    const desk = new THREE.Mesh(
      new THREE.BoxGeometry(1.5, 0.08, 0.7),
      this.M(0x556677, 0.55, 0.1)
    );
    desk.position.set(x, 0.6, z);
    desk.rotation.z = 1.4;
    desk.castShadow = false;
    this.add(desk);
  }

  // ==================== DOOR ZOMBIE (Normal zombie for intro cutscene) ====================
  private addDoorZombie(x: number, y: number, z: number): void {
    const s = 1.3; // head at ~1.69 aligns with window at y=1.7

    // --- Materials (dark grey-brown, decayed flesh) ---
    const skinMat = new THREE.MeshStandardMaterial({ color: 0x3a3230, roughness: 0.8 });
    const darkFlesh = new THREE.MeshStandardMaterial({ color: 0x2a2422, roughness: 0.85 });
    const paleFlesh = new THREE.MeshStandardMaterial({ color: 0x4a4038, roughness: 0.75 });
    const clothMat = new THREE.MeshStandardMaterial({ color: 0x1a1815, roughness: 0.92 });
    const clothLight = new THREE.MeshStandardMaterial({ color: 0x2a2825, roughness: 0.88 });
    const eyeMat = new THREE.MeshStandardMaterial({
      color: 0xcc8800, emissive: 0xff9900, emissiveIntensity: 3,
    });
    const woundMat = new THREE.MeshStandardMaterial({
      color: 0x551111, roughness: 0.5, emissive: 0x220000, emissiveIntensity: 0.15,
    });
    const boneMat = new THREE.MeshStandardMaterial({ color: 0xbbb8a4, roughness: 0.55 });
    const teethMat = new THREE.MeshStandardMaterial({ color: 0x887755, roughness: 0.45 });
    const gumMat = new THREE.MeshStandardMaterial({ color: 0x3a2222, roughness: 0.65 });

    const g = new THREE.Group();

    // === TORSO (emaciated, skeletal) ===
    // Ribcage (narrow, bony)
    const ribcage = new THREE.Mesh(new THREE.SphereGeometry(0.16 * s, 10, 8), skinMat);
    ribcage.scale.set(1, 1.4, 0.65);
    ribcage.position.y = 0.95 * s;
    g.add(ribcage);

    // Visible ribs (protruding through skin)
    for (let i = 0; i < 5; i++) {
      for (const side of [-1, 1]) {
        const rib = new THREE.Mesh(
          new THREE.TorusGeometry(0.08 * s, 0.005 * s, 4, 8, Math.PI * 0.5),
          paleFlesh
        );
        rib.position.set(side * 0.02 * s, (0.82 + i * 0.06) * s, -0.06 * s);
        rib.rotation.y = side > 0 ? 0 : Math.PI;
        rib.rotation.z = side * 0.2;
        g.add(rib);
      }
    }

    // Sunken belly (concave, starving look)
    const belly = new THREE.Mesh(new THREE.SphereGeometry(0.12 * s, 8, 6), darkFlesh);
    belly.scale.set(0.9, 0.8, 0.6);
    belly.position.set(0, 0.68 * s, -0.02 * s);
    g.add(belly);

    // Tattered shirt/rags hanging off shoulders
    const ragMain = new THREE.Mesh(new THREE.SphereGeometry(0.18 * s, 8, 6), clothMat);
    ragMain.scale.set(1.1, 1.5, 0.5);
    ragMain.position.set(0, 0.9 * s, 0.04 * s);
    g.add(ragMain);

    // Shoulders (bony, protruding)
    for (const side of [-1, 1]) {
      const shoulder = new THREE.Mesh(new THREE.SphereGeometry(0.06 * s, 8, 6), skinMat);
      shoulder.scale.set(1.3, 0.6, 0.8);
      shoulder.position.set(side * 0.22 * s, 1.12 * s, -0.02 * s);
      g.add(shoulder);
      // Collarbone visible
      const clavicle = new THREE.Mesh(
        new THREE.CylinderGeometry(0.006 * s, 0.006 * s, 0.18 * s, 4),
        paleFlesh
      );
      clavicle.position.set(side * 0.1 * s, 1.08 * s, -0.08 * s);
      clavicle.rotation.z = side * 0.8;
      g.add(clavicle);
    }

    // Spine visible on back
    for (let i = 0; i < 6; i++) {
      const vertebra = new THREE.Mesh(new THREE.SphereGeometry(0.012 * s, 5, 4), paleFlesh);
      vertebra.position.set(0, (0.6 + i * 0.08) * s, 0.1 * s);
      g.add(vertebra);
    }

    // === PELVIS / HIPS (bony) ===
    const hips = new THREE.Mesh(new THREE.SphereGeometry(0.13 * s, 8, 6), skinMat);
    hips.scale.set(1, 0.6, 0.6);
    hips.position.y = 0.5 * s;
    g.add(hips);

    // Hip bones protruding
    for (const side of [-1, 1]) {
      const hipBone = new THREE.Mesh(new THREE.SphereGeometry(0.025 * s, 5, 4), paleFlesh);
      hipBone.scale.set(1.5, 0.8, 0.8);
      hipBone.position.set(side * 0.12 * s, 0.52 * s, -0.06 * s);
      g.add(hipBone);
    }

    // === HEAD (gaunt, skull-like) ===
    // Skull
    const skull = new THREE.Mesh(new THREE.SphereGeometry(0.15 * s, 12, 10), skinMat);
    skull.scale.set(1, 1.2, 1);
    skull.position.y = 1.3 * s;
    skull.userData.isHead = true;
    g.add(skull);

    // Temporal ridges (skull shape detail)
    for (const side of [-1, 1]) {
      const temple = new THREE.Mesh(new THREE.SphereGeometry(0.08 * s, 6, 5), skinMat);
      temple.scale.set(0.4, 1.0, 0.8);
      temple.position.set(side * 0.12 * s, 1.32 * s, 0);
      g.add(temple);
    }

    // Heavy brow ridge
    const brow = new THREE.Mesh(new THREE.SphereGeometry(0.1 * s, 8, 5), darkFlesh);
    brow.scale.set(1.3, 0.25, 0.5);
    brow.position.set(0, 1.35 * s, -0.1 * s);
    g.add(brow);

    // Sunken cheeks (concave hollows under cheekbones)
    for (const side of [-1, 1]) {
      // Cheekbone (protruding)
      const cheekBone = new THREE.Mesh(new THREE.SphereGeometry(0.035 * s, 6, 5), paleFlesh);
      cheekBone.scale.set(1, 0.6, 0.5);
      cheekBone.position.set(side * 0.1 * s, 1.25 * s, -0.1 * s);
      g.add(cheekBone);
      // Hollow below (dark shadow)
      const hollow = new THREE.Mesh(new THREE.SphereGeometry(0.03 * s, 5, 4), darkFlesh);
      hollow.scale.set(1, 1.2, 0.3);
      hollow.position.set(side * 0.08 * s, 1.2 * s, -0.1 * s);
      g.add(hollow);
    }

    // Deep eye sockets (very dark, skull-like)
    const socketMat = new THREE.MeshStandardMaterial({ color: 0x0a0808, roughness: 0.95 });
    for (const side of [-1, 1]) {
      const socket = new THREE.Mesh(new THREE.SphereGeometry(0.035 * s, 8, 6), socketMat);
      socket.position.set(side * 0.055 * s, 1.3 * s, -0.1 * s);
      g.add(socket);
    }

    // Eyes (orange glow deep inside sockets)
    for (const side of [-1, 1]) {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.018 * s, 8, 6), eyeMat);
      eye.position.set(side * 0.055 * s, 1.3 * s, -0.12 * s);
      g.add(eye);
    }
    const eyeGlow = new THREE.PointLight(0xff9900, 1.5, 3);
    eyeGlow.position.set(0, 1.3 * s, -0.15 * s);
    g.add(eyeGlow);

    // Nose (mostly gone — just a cavity)
    const noseHole = new THREE.Mesh(new THREE.SphereGeometry(0.015 * s, 5, 4), socketMat);
    noseHole.scale.set(0.8, 1.2, 0.8);
    noseHole.position.set(0, 1.23 * s, -0.13 * s);
    g.add(noseHole);
    // Nasal bone remnant
    const nasalBone = new THREE.Mesh(new THREE.SphereGeometry(0.01 * s, 4, 3), boneMat);
    nasalBone.scale.set(0.6, 1.5, 0.8);
    nasalBone.position.set(0, 1.25 * s, -0.14 * s);
    g.add(nasalBone);

    // Jaw (wide open mouth — aggressive snarl)
    const jawBone = new THREE.Mesh(new THREE.SphereGeometry(0.09 * s, 8, 6), skinMat);
    jawBone.scale.set(1.1, 0.4, 0.7);
    jawBone.position.set(0, 1.13 * s, -0.05 * s);
    jawBone.rotation.x = 0.35; // mouth wide open
    jawBone.userData.isHead = true;
    g.add(jawBone);

    // Mouth interior (dark cavity)
    const mouthCavity = new THREE.Mesh(new THREE.SphereGeometry(0.05 * s, 6, 5), socketMat);
    mouthCavity.scale.set(1.3, 0.8, 0.6);
    mouthCavity.position.set(0, 1.17 * s, -0.09 * s);
    g.add(mouthCavity);

    // Gums
    const upperGum = new THREE.Mesh(new THREE.SphereGeometry(0.05 * s, 6, 4), gumMat);
    upperGum.scale.set(1.1, 0.25, 0.5);
    upperGum.position.set(0, 1.19 * s, -0.1 * s);
    g.add(upperGum);

    // Teeth (crooked, some missing, yellowish)
    for (const ty of [1.19, 1.14]) { // upper and lower row
      for (let i = -2; i <= 2; i++) {
        if (Math.random() > 0.25) {
          const h = 0.012 + Math.random() * 0.008;
          const tooth = new THREE.Mesh(
            new THREE.ConeGeometry(0.005 * s, h * s, 4),
            teethMat
          );
          tooth.position.set(i * 0.017 * s, ty * s, -0.12 * s);
          tooth.rotation.x = ty > 1.16 ? Math.PI : 0;
          tooth.rotation.z = (Math.random() - 0.5) * 0.4;
          g.add(tooth);
        }
      }
    }

    // Ears (one torn off, one remaining)
    const ear = new THREE.Mesh(new THREE.SphereGeometry(0.02 * s, 5, 4), darkFlesh);
    ear.scale.set(0.35, 0.9, 0.6);
    ear.position.set(-0.14 * s, 1.28 * s, -0.01 * s);
    g.add(ear);
    // Torn ear stump on other side
    const earStump = new THREE.Mesh(new THREE.SphereGeometry(0.01 * s, 4, 3), woundMat);
    earStump.position.set(0.14 * s, 1.28 * s, -0.01 * s);
    g.add(earStump);

    // Neck (thin, sinewy, tendons visible)
    const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.045 * s, 0.06 * s, 0.14 * s, 8), skinMat);
    neck.position.y = 1.12 * s;
    g.add(neck);
    // Prominent tendons
    for (const side of [-1, 1]) {
      const tendon = new THREE.Mesh(
        new THREE.CylinderGeometry(0.006 * s, 0.008 * s, 0.16 * s, 4),
        paleFlesh
      );
      tendon.position.set(side * 0.035 * s, 1.1 * s, -0.02 * s);
      tendon.rotation.z = side * 0.12;
      g.add(tendon);
    }
    // Throat bulge (Adam's apple)
    const throat = new THREE.Mesh(new THREE.SphereGeometry(0.012 * s, 5, 4), skinMat);
    throat.position.set(0, 1.1 * s, -0.05 * s);
    g.add(throat);

    // === ARMS (long, skeletal, reaching) ===
    for (const side of [-1, 1]) {
      // Upper arm (very thin)
      const upperArm = new THREE.Mesh(
        new THREE.CylinderGeometry(0.035 * s, 0.028 * s, 0.36 * s, 8),
        skinMat
      );
      upperArm.position.set(side * 0.26 * s, 0.88 * s, -0.1 * s);
      upperArm.rotation.x = -0.5;
      upperArm.rotation.z = side * 0.15;
      upperArm.userData.isLeftArm = side === -1;
      upperArm.userData.isRightArm = side === 1;
      g.add(upperArm);

      // Elbow (bony, protruding)
      const elbow = new THREE.Mesh(new THREE.SphereGeometry(0.022 * s, 5, 4), paleFlesh);
      elbow.position.set(side * 0.32 * s, 0.68 * s, -0.26 * s);
      g.add(elbow);

      // Forearm (even thinner, bones visible under skin)
      const forearm = new THREE.Mesh(
        new THREE.CylinderGeometry(0.025 * s, 0.018 * s, 0.32 * s, 8),
        skinMat
      );
      forearm.position.set(side * 0.35 * s, 0.5 * s, -0.35 * s);
      forearm.rotation.x = -0.6;
      g.add(forearm);

      // Ulna bone visible
      const ulna = new THREE.Mesh(
        new THREE.CylinderGeometry(0.004 * s, 0.004 * s, 0.25 * s, 4),
        paleFlesh
      );
      ulna.position.set(side * 0.36 * s, 0.52 * s, -0.32 * s);
      ulna.rotation.x = -0.6;
      g.add(ulna);

      // Wrist
      const wrist = new THREE.Mesh(new THREE.SphereGeometry(0.015 * s, 5, 4), paleFlesh);
      wrist.position.set(side * 0.37 * s, 0.35 * s, -0.45 * s);
      g.add(wrist);

      // Hand (skeletal, bony, spread fingers)
      const palm = new THREE.Mesh(new THREE.SphereGeometry(0.022 * s, 6, 5), darkFlesh);
      palm.scale.set(1.3, 0.4, 1);
      palm.position.set(side * 0.37 * s, 0.32 * s, -0.47 * s);
      g.add(palm);

      // Long claw-like fingers (5)
      for (let f = -2; f <= 2; f++) {
        const fingerLen = 0.04 + Math.random() * 0.015;
        const finger = new THREE.Mesh(
          new THREE.CylinderGeometry(0.004 * s, 0.002 * s, fingerLen * s, 4),
          darkFlesh
        );
        finger.position.set(
          side * 0.37 * s + f * 0.01 * s,
          0.28 * s,
          -0.5 * s
        );
        finger.rotation.x = -0.4 + f * 0.05;
        finger.rotation.z = side * f * 0.08;
        g.add(finger);
      }

      // Rag hanging from shoulder
      const rag = new THREE.Mesh(
        new THREE.PlaneGeometry(0.08 * s, 0.2 * s),
        clothLight
      );
      rag.position.set(side * 0.24 * s, 0.95 * s, -0.04 * s);
      rag.rotation.z = side * 0.3;
      g.add(rag);
    }

    // === LEGS (thin, bony) ===
    for (const side of [-1, 1]) {
      // Thigh
      const thigh = new THREE.Mesh(
        new THREE.CylinderGeometry(0.05 * s, 0.035 * s, 0.38 * s, 8),
        clothMat
      );
      thigh.position.set(side * 0.09 * s, 0.28 * s, 0);
      thigh.userData.isLeg = true;
      g.add(thigh);

      // Kneecap (bony)
      const knee = new THREE.Mesh(new THREE.SphereGeometry(0.025 * s, 5, 4), paleFlesh);
      knee.position.set(side * 0.09 * s, 0.08 * s, -0.02 * s);
      g.add(knee);

      // Shin (very thin)
      const shin = new THREE.Mesh(
        new THREE.CylinderGeometry(0.028 * s, 0.02 * s, 0.32 * s, 6),
        skinMat
      );
      shin.position.set(side * 0.09 * s, -0.1 * s, 0);
      g.add(shin);

      // Bare foot (bony toes)
      const foot = new THREE.Mesh(new THREE.SphereGeometry(0.025 * s, 5, 4), darkFlesh);
      foot.scale.set(0.7, 0.3, 1.6);
      foot.position.set(side * 0.09 * s, -0.26 * s, -0.02 * s);
      g.add(foot);
    }

    // Tattered pants remnants
    for (const side of [-1, 1]) {
      const pantRag = new THREE.Mesh(
        new THREE.PlaneGeometry(0.06 * s, 0.15 * s),
        clothMat
      );
      pantRag.position.set(side * 0.1 * s, 0.15 * s, -0.03 * s);
      pantRag.rotation.z = side * 0.2;
      g.add(pantRag);
    }

    // === GORE & DECAY ===
    // Large open wound on chest (dark cavity)
    const chestWound = new THREE.Mesh(new THREE.SphereGeometry(0.04 * s, 6, 5), woundMat);
    chestWound.scale.set(2, 1.2, 0.3);
    chestWound.position.set(0.06 * s, 0.88 * s, -0.12 * s);
    g.add(chestWound);

    // Bite/scratch marks
    for (let i = 0; i < 4; i++) {
      const mark = new THREE.Mesh(new THREE.SphereGeometry(0.01 * s, 5, 4), woundMat);
      mark.scale.set(1.5, 1, 0.3);
      mark.position.set(
        (Math.random() - 0.5) * 0.25 * s,
        0.55 * s + Math.random() * 0.5 * s,
        -0.1 * s
      );
      g.add(mark);
    }

    // Blood dripping from mouth/chin
    for (let i = 0; i < 3; i++) {
      const drip = new THREE.Mesh(
        new THREE.CylinderGeometry(0.004 * s, 0.002 * s, (0.03 + Math.random() * 0.04) * s, 4),
        woundMat
      );
      drip.position.set(
        (Math.random() - 0.5) * 0.04 * s,
        (1.06 - i * 0.03) * s,
        -0.1 * s
      );
      g.add(drip);
    }

    // Hanging cloth strips / tattered rags
    for (let i = 0; i < 5; i++) {
      const strip = new THREE.Mesh(
        new THREE.PlaneGeometry(0.025 * s, (0.1 + Math.random() * 0.15) * s),
        i % 2 === 0 ? clothMat : clothLight
      );
      const angle = -0.8 + Math.random() * 1.6;
      strip.position.set(
        Math.sin(angle) * 0.16 * s,
        0.5 * s + Math.random() * 0.4 * s,
        Math.cos(angle) * 0.08 * s
      );
      strip.rotation.z = (Math.random() - 0.5) * 0.5;
      strip.rotation.y = angle;
      g.add(strip);
    }

    // Hair (stringy, hanging down — like reference)
    const hairMat = this.M(0x151210, 0.92);
    // Stringy strands hanging from scalp
    for (let i = 0; i < 8; i++) {
      const strand = new THREE.Mesh(
        new THREE.CylinderGeometry(0.003 * s, 0.001 * s, (0.08 + Math.random() * 0.12) * s, 3),
        hairMat
      );
      const a = -1.5 + Math.random() * 3;
      strand.position.set(
        Math.sin(a) * 0.13 * s,
        1.35 * s,
        Math.cos(a) * 0.1 * s
      );
      strand.rotation.z = (Math.random() - 0.5) * 0.6;
      strand.rotation.x = Math.random() * 0.3;
      g.add(strand);
    }
    // Matted patches on top
    for (let i = 0; i < 3; i++) {
      const patch = new THREE.Mesh(new THREE.SphereGeometry(0.03 * s, 4, 3), hairMat);
      patch.scale.set(1, 0.2, 1);
      const a = Math.random() * Math.PI * 2;
      patch.position.set(Math.sin(a) * 0.1 * s, 1.42 * s, Math.cos(a) * 0.08 * s);
      g.add(patch);
    }

    // Hunched forward posture (like reference image)
    // Wrap body parts in a sub-group for posture, keep outer group for animation
    g.position.set(x, y, z);
    g.userData.isZombie = true;
    g.userData.isDoorZombie = true;
    this.add(g);
  }

  // ==================== BOSS MODEL (High Quality) ====================
  private addBoss(x: number, y: number, z: number): void {
    const s = 1.0;
    const skinCol = 0x3a4a3a;
    const mat = this.skinMat(skinCol);
    const darkSkin = this.skinMat(0x2a3a2a);
    const boneMat = new THREE.MeshStandardMaterial({ color: 0xddd8c4, roughness: 0.45, metalness: 0.05 });
    const eyeMat = new THREE.MeshStandardMaterial({
      color: 0xff0000, emissive: 0xff0000, emissiveIntensity: 5,
    });
    const crownMat = new THREE.MeshStandardMaterial({
      color: 0xffd700, emissive: 0xffd700, emissiveIntensity: 0.8,
      metalness: 0.8, roughness: 0.2,
    });
    const woundMat = new THREE.MeshStandardMaterial({
      color: 0x881111, roughness: 0.4, emissive: 0x440000, emissiveIntensity: 0.3,
    });
    const veinMat = new THREE.MeshStandardMaterial({
      color: 0x332244, roughness: 0.7, emissive: 0x110022, emissiveIntensity: 0.1,
    });
    const teethMat = new THREE.MeshStandardMaterial({ color: 0xccbb88, roughness: 0.35, metalness: 0.05 });

    const g = new THREE.Group();

    // === TORSO (tapered, muscular) ===
    // Upper torso (chest)
    const chest = new THREE.Mesh(
      new THREE.BoxGeometry(0.52 * s, 0.5 * s, 0.32 * s),
      mat
    );
    chest.position.y = 1.15 * s;
    chest.castShadow = false;
    g.add(chest);

    // Pectoral definition
    for (const side of [-1, 1]) {
      const pec = new THREE.Mesh(
        new THREE.SphereGeometry(0.1 * s, 12, 8),
        mat
      );
      pec.scale.set(1.2, 0.7, 0.5);
      pec.position.set(side * 0.12 * s, 1.22 * s, -0.14 * s);
      g.add(pec);
    }

    // Lower torso (abdomen)
    const abdomen = new THREE.Mesh(
      new THREE.BoxGeometry(0.44 * s, 0.35 * s, 0.28 * s),
      mat
    );
    abdomen.position.y = 0.78 * s;
    g.add(abdomen);

    // Waist/pelvis
    const waist = new THREE.Mesh(
      new THREE.BoxGeometry(0.38 * s, 0.2 * s, 0.26 * s),
      darkSkin
    );
    waist.position.y = 0.55 * s;
    g.add(waist);

    // === HEAD (detailed skull shape) ===
    // Cranium
    const cranium = new THREE.Mesh(
      new THREE.SphereGeometry(0.17 * s, 20, 16),
      mat
    );
    cranium.position.y = 1.72 * s;
    cranium.scale.set(1, 1.15, 1.05);
    cranium.castShadow = false;
    g.add(cranium);

    // Jaw (separate piece, slightly open)
    const jaw = new THREE.Mesh(
      new THREE.BoxGeometry(0.2 * s, 0.07 * s, 0.14 * s),
      mat
    );
    jaw.position.set(0, 1.56 * s, -0.04 * s);
    jaw.rotation.x = 0.12;
    g.add(jaw);

    // Brow ridge
    const brow = new THREE.Mesh(
      new THREE.BoxGeometry(0.24 * s, 0.035 * s, 0.08 * s),
      darkSkin
    );
    brow.position.set(0, 1.78 * s, -0.14 * s);
    g.add(brow);

    // Cheekbones
    for (const side of [-1, 1]) {
      const cheek = new THREE.Mesh(
        new THREE.SphereGeometry(0.04 * s, 8, 6),
        mat
      );
      cheek.scale.set(1.3, 0.8, 0.6);
      cheek.position.set(side * 0.1 * s, 1.66 * s, -0.13 * s);
      g.add(cheek);
    }

    // Eye sockets (dark recesses)
    const socketMat = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a, roughness: 0.9,
    });
    for (const side of [-1, 1]) {
      const socket = new THREE.Mesh(
        new THREE.SphereGeometry(0.035 * s, 10, 8),
        socketMat
      );
      socket.position.set(side * 0.065 * s, 1.73 * s, -0.15 * s);
      g.add(socket);
    }

    // Eyes (glowing red, with inner glow)
    for (const side of [-1, 1]) {
      const eye = new THREE.Mesh(
        new THREE.SphereGeometry(0.028 * s, 12, 10),
        eyeMat
      );
      eye.position.set(side * 0.065 * s, 1.73 * s, -0.16 * s);
      g.add(eye);

      // Iris/pupil detail
      const pupil = new THREE.Mesh(
        new THREE.SphereGeometry(0.012 * s, 8, 6),
        new THREE.MeshStandardMaterial({
          color: 0x000000, emissive: 0xff2200, emissiveIntensity: 2,
        })
      );
      pupil.position.set(side * 0.065 * s, 1.73 * s, -0.18 * s);
      g.add(pupil);
    }

    // Eye glow light
    const eyeGlow = new THREE.PointLight(0xff0000, 2, 3);
    eyeGlow.position.set(0, 1.73 * s, -0.2 * s);
    g.add(eyeGlow);

    // Nose (broken/disfigured)
    const nose = new THREE.Mesh(
      new THREE.BoxGeometry(0.035 * s, 0.05 * s, 0.04 * s),
      darkSkin
    );
    nose.position.set(0.005 * s, 1.66 * s, -0.17 * s);
    nose.rotation.z = 0.1;
    g.add(nose);

    // Teeth (visible through slightly open mouth)
    for (let i = -2; i <= 2; i++) {
      const tooth = new THREE.Mesh(
        new THREE.BoxGeometry(0.018 * s, 0.025 * s, 0.015 * s),
        teethMat
      );
      tooth.position.set(i * 0.022 * s, 1.575 * s, -0.12 * s);
      g.add(tooth);
    }

    // Neck (thick, with tendons)
    const neck = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07 * s, 0.09 * s, 0.15 * s, 12),
      mat
    );
    neck.position.y = 1.5 * s;
    g.add(neck);

    // Neck tendons
    for (const side of [-1, 1]) {
      const tendon = new THREE.Mesh(
        new THREE.CylinderGeometry(0.012 * s, 0.015 * s, 0.18 * s, 6),
        darkSkin
      );
      tendon.position.set(side * 0.05 * s, 1.48 * s, -0.04 * s);
      tendon.rotation.z = side * 0.15;
      g.add(tendon);
    }

    // === CROWN/HORNS ===
    for (const side of [-1, 1]) {
      const horn = new THREE.Mesh(
        new THREE.ConeGeometry(0.04 * s, 0.3 * s, 8),
        crownMat
      );
      horn.position.set(side * 0.12 * s, 1.95 * s, -0.03 * s);
      horn.rotation.z = -side * 0.35;
      horn.castShadow = false;
      g.add(horn);
    }
    const band = new THREE.Mesh(
      new THREE.TorusGeometry(0.15 * s, 0.015 * s, 8, 20),
      crownMat
    );
    band.position.y = 1.88 * s;
    band.rotation.x = Math.PI / 2;
    g.add(band);

    // === ARMS (with upper/lower + hands) ===
    for (const side of [-1, 1]) {
      // Shoulder joint
      const shoulder = new THREE.Mesh(
        new THREE.SphereGeometry(0.06 * s, 10, 8),
        mat
      );
      shoulder.position.set(side * 0.32 * s, 1.32 * s, -0.05 * s);
      g.add(shoulder);

      // Upper arm (with muscle definition)
      const upperArm = new THREE.Mesh(
        new THREE.CylinderGeometry(0.065 * s, 0.055 * s, 0.35 * s, 10),
        mat
      );
      upperArm.position.set(side * 0.38 * s, 1.1 * s, -0.12 * s);
      upperArm.rotation.x = -0.35;
      upperArm.rotation.z = side * 0.1;
      upperArm.castShadow = false;
      upperArm.userData.isLeftArm = side === -1;
      upperArm.userData.isRightArm = side === 1;
      g.add(upperArm);

      // Elbow joint
      const elbow = new THREE.Mesh(
        new THREE.SphereGeometry(0.04 * s, 8, 6),
        darkSkin
      );
      elbow.position.set(side * 0.42 * s, 0.88 * s, -0.25 * s);
      g.add(elbow);

      // Forearm
      const forearm = new THREE.Mesh(
        new THREE.CylinderGeometry(0.055 * s, 0.04 * s, 0.32 * s, 10),
        mat
      );
      forearm.position.set(side * 0.44 * s, 0.7 * s, -0.32 * s);
      forearm.rotation.x = -0.5;
      forearm.castShadow = false;
      g.add(forearm);

      // Veins on forearm
      for (let v = 0; v < 2; v++) {
        const vein = new THREE.Mesh(
          new THREE.CylinderGeometry(0.005 * s, 0.004 * s, 0.2 * s, 4),
          veinMat
        );
        vein.position.set(
          side * (0.44 + v * 0.015) * s,
          0.72 * s,
          (-0.28 - v * 0.03) * s
        );
        vein.rotation.x = -0.5;
        vein.rotation.z = (Math.random() - 0.5) * 0.2;
        g.add(vein);
      }

      // Hand (with fingers)
      const hand = new THREE.Mesh(
        new THREE.BoxGeometry(0.06 * s, 0.08 * s, 0.035 * s),
        darkSkin
      );
      hand.position.set(side * 0.45 * s, 0.5 * s, -0.42 * s);
      hand.rotation.x = -0.6;
      g.add(hand);

      // Fingers/claws
      for (let f = -1; f <= 1; f++) {
        const claw = new THREE.Mesh(
          new THREE.ConeGeometry(0.01 * s, 0.07 * s, 5),
          boneMat
        );
        claw.position.set(
          side * 0.45 * s + f * 0.018 * s,
          0.44 * s,
          -0.46 * s
        );
        claw.rotation.x = -0.7;
        g.add(claw);
      }
    }

    // === LEGS (with knee joints) ===
    for (const side of [-1, 1]) {
      // Hip joint
      const hip = new THREE.Mesh(
        new THREE.SphereGeometry(0.05 * s, 8, 6),
        darkSkin
      );
      hip.position.set(side * 0.12 * s, 0.48 * s, 0);
      g.add(hip);

      // Upper leg
      const upperLeg = new THREE.Mesh(
        new THREE.CylinderGeometry(0.08 * s, 0.06 * s, 0.4 * s, 10),
        mat
      );
      upperLeg.position.set(side * 0.14 * s, 0.3 * s, 0);
      upperLeg.castShadow = false;
      upperLeg.userData.isLeg = true;
      g.add(upperLeg);

      // Knee
      const knee = new THREE.Mesh(
        new THREE.SphereGeometry(0.045 * s, 8, 6),
        darkSkin
      );
      knee.position.set(side * 0.14 * s, 0.1 * s, 0);
      knee.userData.isLeg = true;
      g.add(knee);

      // Lower leg
      const lowerLeg = new THREE.Mesh(
        new THREE.CylinderGeometry(0.06 * s, 0.045 * s, 0.35 * s, 10),
        mat
      );
      lowerLeg.position.set(side * 0.14 * s, -0.1 * s, 0);
      lowerLeg.castShadow = false;
      g.add(lowerLeg);

      // Foot
      const foot = new THREE.Mesh(
        new THREE.BoxGeometry(0.08 * s, 0.03 * s, 0.12 * s),
        darkSkin
      );
      foot.position.set(side * 0.14 * s, -0.27 * s, -0.02 * s);
      g.add(foot);
    }

    // === WOUNDS/GORE ===
    for (let i = 0; i < 5; i++) {
      const wound = new THREE.Mesh(
        new THREE.CircleGeometry(0.03 * s + Math.random() * 0.03 * s, 8),
        woundMat
      );
      wound.position.set(
        (Math.random() - 0.5) * 0.4 * s,
        0.6 * s + Math.random() * 0.8 * s,
        -0.17 * s
      );
      wound.rotation.y = Math.PI / 2;
      g.add(wound);
    }

    // Exposed ribs
    for (let i = 0; i < 4; i++) {
      const rib = new THREE.Mesh(
        new THREE.TorusGeometry(0.08 * s, 0.006 * s, 4, 12, Math.PI * 0.6),
        boneMat
      );
      rib.position.set(0.12 * s, (0.85 + i * 0.08) * s, -0.08 * s);
      rib.rotation.y = Math.PI / 2;
      rib.rotation.z = 0.3;
      g.add(rib);
    }

    // Veins on torso
    for (let i = 0; i < 4; i++) {
      const vein = new THREE.Mesh(
        new THREE.CylinderGeometry(0.004 * s, 0.003 * s, 0.15 * s, 4),
        veinMat
      );
      vein.position.set(
        (Math.random() - 0.5) * 0.35 * s,
        0.8 * s + Math.random() * 0.5 * s,
        -0.16 * s
      );
      vein.rotation.z = (Math.random() - 0.5) * 0.8;
      g.add(vein);
    }

    // Boss aura (reddish glow)
    const aura = new THREE.PointLight(0xff2200, 2.5, 6);
    aura.position.set(0, 1.2 * s, 0);
    g.add(aura);

    g.position.set(x, y, z);
    g.userData.isZombie = true;
    g.userData.isBoss = true;
    this.add(g);
  }

  // ==================== DEAD RESEARCHER (Intro Cutscene) ====================
  private addDeadResearcher(
    x: number, z: number,
    pose: 'side' | 'face_down' | 'back' | 'slumped_wall',
    rotY: number
  ): void {
    const skinTone = 0xd4a574 + Math.floor(Math.random() * 0x101010 - 0x080808);
    const skinMat = this.skinMat(skinTone);
    const paleSkin = this.skinMat(skinTone - 0x303020); // blood loss pallor

    // Clothing: lab coats, scrubs, business casual
    const outfitType = Math.floor(Math.random() * 3);
    const clothColors = [
      [0xdddddd, 0x3355aa], // white lab coat + blue scrubs
      [0xcccccc, 0x2a6a3a], // light coat + green scrubs
      [0x445566, 0x333333], // dark shirt + black pants
    ];
    const [topColor, bottomColor] = clothColors[outfitType];
    const topMat = this.M(topColor, 0.75);
    const bottomMat = this.M(bottomColor, 0.8);
    const shoeMat = this.M(0x222222, 0.7, 0.1);
    const hairColor = [0x2a1a0a, 0x4a3a1a, 0x1a1a1a, 0x6a4a2a][Math.floor(Math.random() * 4)];
    const hairMat = this.M(hairColor, 0.85);

    const bloodMat = new THREE.MeshStandardMaterial({
      color: 0x8b0000, roughness: 0.35, metalness: 0.05,
      emissive: 0x330000, emissiveIntensity: 0.15,
    });

    const g = new THREE.Group();

    // === HEAD ===
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.13, 16, 12), paleSkin);
    head.scale.set(1, 1.1, 1);
    head.position.y = 1.55;
    g.add(head);

    // Hair
    const hair = new THREE.Mesh(
      new THREE.SphereGeometry(0.135, 12, 10, 0, Math.PI * 2, 0, Math.PI * 0.55),
      hairMat
    );
    hair.position.y = 1.58;
    g.add(hair);

    // Eyes (closed - thin line)
    for (const side of [-1, 1]) {
      const lid = new THREE.Mesh(
        new THREE.BoxGeometry(0.04, 0.008, 0.02),
        this.M(skinTone - 0x202010, 0.8)
      );
      lid.position.set(side * 0.045, 1.56, -0.12);
      g.add(lid);
    }

    // Nose
    const nose = new THREE.Mesh(
      new THREE.BoxGeometry(0.025, 0.035, 0.03),
      skinMat
    );
    nose.position.set(0, 1.52, -0.13);
    g.add(nose);

    // Mouth (slightly open)
    const mouth = new THREE.Mesh(
      new THREE.BoxGeometry(0.04, 0.012, 0.015),
      this.M(0x553333, 0.6)
    );
    mouth.position.set(0, 1.47, -0.12);
    g.add(mouth);

    // Ears
    for (const side of [-1, 1]) {
      const ear = new THREE.Mesh(
        new THREE.SphereGeometry(0.025, 6, 5),
        skinMat
      );
      ear.scale.set(0.5, 1, 0.7);
      ear.position.set(side * 0.125, 1.55, -0.02);
      g.add(ear);
    }

    // === NECK ===
    const neck = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.055, 0.1, 10),
      paleSkin
    );
    neck.position.y = 1.43;
    g.add(neck);

    // === TORSO ===
    // Upper torso (chest)
    const chest = new THREE.Mesh(
      new THREE.BoxGeometry(0.4, 0.35, 0.22),
      topMat
    );
    chest.position.y = 1.2;
    chest.castShadow = false;
    g.add(chest);

    // Lower torso
    const abdomen = new THREE.Mesh(
      new THREE.BoxGeometry(0.36, 0.25, 0.2),
      topMat
    );
    abdomen.position.y = 0.93;
    g.add(abdomen);

    // Lab coat details (collar, buttons)
    if (outfitType <= 1) {
      // Collar flaps
      for (const side of [-1, 1]) {
        const collar = new THREE.Mesh(
          new THREE.BoxGeometry(0.08, 0.06, 0.015),
          this.M(topColor - 0x111111, 0.7)
        );
        collar.position.set(side * 0.08, 1.37, -0.11);
        collar.rotation.z = side * 0.3;
        g.add(collar);
      }
      // Buttons
      for (let by = 1.05; by <= 1.3; by += 0.08) {
        const btn = new THREE.Mesh(
          new THREE.CylinderGeometry(0.008, 0.008, 0.005, 8),
          this.M(0x888888, 0.3, 0.5)
        );
        btn.rotation.x = Math.PI / 2;
        btn.position.set(0, by, -0.112);
        g.add(btn);
      }
      // Coat bottom (below waist)
      const coatBottom = new THREE.Mesh(
        new THREE.BoxGeometry(0.38, 0.3, 0.18),
        topMat
      );
      coatBottom.position.y = 0.7;
      g.add(coatBottom);
    }

    // Chest pocket (with pen)
    const pocket = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 0.06, 0.005),
      this.M(topColor - 0x080808, 0.75)
    );
    pocket.position.set(-0.12, 1.25, -0.112);
    g.add(pocket);
    // Pen
    const pen = new THREE.Mesh(
      new THREE.CylinderGeometry(0.004, 0.004, 0.07, 6),
      this.M(0x111188, 0.5, 0.3)
    );
    pen.position.set(-0.11, 1.28, -0.115);
    pen.rotation.z = 0.1;
    g.add(pen);

    // ID badge
    const badge = new THREE.Mesh(
      new THREE.BoxGeometry(0.05, 0.07, 0.003),
      this.M(0xeeeeee, 0.6)
    );
    badge.position.set(0.1, 1.1, -0.112);
    g.add(badge);
    // Badge clip
    const clip = new THREE.Mesh(
      new THREE.BoxGeometry(0.015, 0.03, 0.006),
      this.M(0x888888, 0.3, 0.7)
    );
    clip.position.set(0.1, 1.15, -0.112);
    g.add(clip);

    // === WAIST / BELT ===
    const belt = new THREE.Mesh(
      new THREE.BoxGeometry(0.37, 0.04, 0.21),
      this.M(0x222222, 0.5, 0.2)
    );
    belt.position.y = 0.8;
    g.add(belt);

    // === ARMS ===
    for (const side of [-1, 1]) {
      // Upper arm
      const upperArm = new THREE.Mesh(
        new THREE.CylinderGeometry(0.05, 0.04, 0.3, 8),
        topMat
      );
      upperArm.position.set(side * 0.25, 1.15, -0.03);
      g.add(upperArm);

      // Forearm (exposed skin below rolled sleeve)
      const forearm = new THREE.Mesh(
        new THREE.CylinderGeometry(0.038, 0.032, 0.28, 8),
        paleSkin
      );
      forearm.position.set(side * 0.28, 0.85, -0.08);
      forearm.rotation.x = -0.2;
      g.add(forearm);

      // Hand
      const hand = new THREE.Mesh(
        new THREE.BoxGeometry(0.05, 0.07, 0.025),
        paleSkin
      );
      hand.position.set(side * 0.3, 0.68, -0.12);
      g.add(hand);

      // Fingers (grouped)
      for (let f = -1; f <= 1; f++) {
        const finger = new THREE.Mesh(
          new THREE.CylinderGeometry(0.006, 0.005, 0.04, 5),
          paleSkin
        );
        finger.position.set(side * 0.3 + f * 0.014, 0.65, -0.13);
        finger.rotation.x = -0.3;
        g.add(finger);
      }

      // Watch (on one arm)
      if (side === -1) {
        const watch = new THREE.Mesh(
          new THREE.BoxGeometry(0.03, 0.025, 0.035),
          this.M(0x333333, 0.3, 0.7)
        );
        watch.position.set(side * 0.29, 0.76, -0.09);
        g.add(watch);
      }
    }

    // === LEGS ===
    for (const side of [-1, 1]) {
      // Upper leg
      const upperLeg = new THREE.Mesh(
        new THREE.CylinderGeometry(0.06, 0.05, 0.4, 8),
        bottomMat
      );
      upperLeg.position.set(side * 0.1, 0.55, 0);
      g.add(upperLeg);

      // Lower leg
      const lowerLeg = new THREE.Mesh(
        new THREE.CylinderGeometry(0.045, 0.038, 0.38, 8),
        bottomMat
      );
      lowerLeg.position.set(side * 0.1, 0.17, 0);
      g.add(lowerLeg);

      // Shoe
      const shoe = new THREE.Mesh(
        new THREE.BoxGeometry(0.07, 0.04, 0.14),
        shoeMat
      );
      shoe.position.set(side * 0.1, -0.01, -0.02);
      g.add(shoe);
    }

    // === BLOOD & WOUNDS ===
    // Wound on body (bite mark / laceration)
    const woundCount = 1 + Math.floor(Math.random() * 2);
    for (let i = 0; i < woundCount; i++) {
      const wound = new THREE.Mesh(
        new THREE.CircleGeometry(0.03 + Math.random() * 0.04, 10),
        bloodMat
      );
      wound.position.set(
        (Math.random() - 0.5) * 0.3,
        0.9 + Math.random() * 0.5,
        -0.113
      );
      g.add(wound);

      // Blood trail from wound
      const trail = new THREE.Mesh(
        new THREE.PlaneGeometry(0.02, 0.1 + Math.random() * 0.15),
        bloodMat
      );
      trail.position.set(
        wound.position.x + (Math.random() - 0.5) * 0.03,
        wound.position.y - 0.12,
        -0.113
      );
      g.add(trail);
    }

    // Blood on face/head (optional)
    if (Math.random() > 0.4) {
      const faceBld = new THREE.Mesh(
        new THREE.PlaneGeometry(0.04, 0.08),
        bloodMat
      );
      faceBld.position.set(0.03, 1.5, -0.132);
      g.add(faceBld);
    }

    // Blood on hands
    for (const side of [-1, 1]) {
      if (Math.random() > 0.3) {
        const handBlood = new THREE.Mesh(
          new THREE.CircleGeometry(0.025, 8),
          bloodMat
        );
        handBlood.position.set(side * 0.3, 0.67, -0.13);
        g.add(handBlood);
      }
    }

    // === APPLY POSE ===
    switch (pose) {
      case 'side':
        // Lying on side
        g.rotation.z = Math.PI / 2;
        g.rotation.y = rotY;
        g.position.set(x, 0.13, z);
        break;
      case 'face_down':
        // Face down on floor
        g.rotation.x = Math.PI / 2;
        g.rotation.y = rotY;
        g.position.set(x, 0.12, z);
        break;
      case 'back':
        // Lying on back
        g.rotation.x = -Math.PI / 2;
        g.rotation.y = rotY;
        g.position.set(x, 0.12, z);
        break;
      case 'slumped_wall':
        // Sitting slumped against wall
        g.position.set(x, -0.4, z > 0 ? 3.6 : -3.6);
        g.rotation.x = -0.3; // leaning back
        g.rotation.y = rotY + (z > 0 ? Math.PI : 0);
        // Head drooped forward
        head.rotation.x = 0.5;
        break;
    }

    this.add(g);

    // === BLOOD POOL on floor ===
    const poolSize = 0.25 + Math.random() * 0.35;
    const pool = new THREE.Mesh(
      new THREE.CircleGeometry(poolSize, 14),
      bloodMat
    );
    pool.rotation.x = -Math.PI / 2;
    pool.position.set(
      pose === 'slumped_wall' ? x : x + (Math.random() - 0.5) * 0.3,
      0.007,
      pose === 'slumped_wall' ? (z > 0 ? 3.3 : -3.3) : z + (Math.random() - 0.5) * 0.3
    );
    this.add(pool);

    // Blood trail from body to pool
    for (let t = 0; t < 3; t++) {
      const splat = new THREE.Mesh(
        new THREE.CircleGeometry(0.04 + Math.random() * 0.06, 8),
        bloodMat
      );
      splat.rotation.x = -Math.PI / 2;
      splat.position.set(
        pool.position.x + (Math.random() - 0.5) * poolSize * 1.5,
        0.006,
        pool.position.z + (Math.random() - 0.5) * poolSize * 1.5
      );
      this.add(splat);
    }
  }

  // ==================== ZOMBIE MODEL (High Quality) ====================
  private addZombie(x: number, y: number, z: number, crouched: boolean): void {
    const skinColor = 0x4a5a3a + Math.floor(Math.random() * 0x101010);
    const mat = this.skinMat(skinColor);
    const clothMat = this.M(skinColor - 0x111111, 0.85);
    const eyeMat = new THREE.MeshStandardMaterial({
      color: 0xffff00, emissive: 0xffff00, emissiveIntensity: 5,
    });
    const woundMat = new THREE.MeshStandardMaterial({
      color: 0x881111, roughness: 0.4, emissive: 0x330000, emissiveIntensity: 0.3,
    });
    const boneMat = new THREE.MeshStandardMaterial({ color: 0xddd8c4, roughness: 0.45 });
    const teethMat = new THREE.MeshStandardMaterial({ color: 0xccbb88, roughness: 0.35 });

    const g = new THREE.Group();
    const s = crouched ? 0.7 : 1;

    // Torso (tapered)
    const torso = new THREE.Mesh(
      new THREE.BoxGeometry(0.4 * s, 0.55 * s, 0.25 * s),
      clothMat
    );
    torso.position.y = 0.85 * s;
    torso.castShadow = false;
    g.add(torso);

    // Waist
    const waist = new THREE.Mesh(
      new THREE.BoxGeometry(0.3 * s, 0.3 * s, 0.2 * s),
      clothMat
    );
    waist.position.y = 0.5 * s;
    g.add(waist);

    // Head (higher poly)
    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.16 * s, 16, 14),
      mat
    );
    head.position.y = 1.3 * s;
    head.scale.set(1, 1.1, 1);
    head.castShadow = false;
    g.add(head);

    // Jaw
    const jaw = new THREE.Mesh(
      new THREE.BoxGeometry(0.18 * s, 0.06 * s, 0.12 * s),
      mat
    );
    jaw.position.set(0, 1.12 * s, -0.06 * s);
    jaw.rotation.x = 0.15;
    g.add(jaw);

    // Brow
    const brow = new THREE.Mesh(
      new THREE.BoxGeometry(0.22 * s, 0.03 * s, 0.06 * s),
      mat
    );
    brow.position.set(0, 1.35 * s, -0.12 * s);
    g.add(brow);

    // Teeth
    const teeth = new THREE.Mesh(
      new THREE.BoxGeometry(0.12 * s, 0.02 * s, 0.03 * s),
      teethMat
    );
    teeth.position.set(0, 1.17 * s, -0.14 * s);
    g.add(teeth);

    // Nose
    const nose = new THREE.Mesh(
      new THREE.BoxGeometry(0.03 * s, 0.04 * s, 0.04 * s),
      mat
    );
    nose.position.set(0, 1.22 * s, -0.17 * s);
    g.add(nose);

    // Eyes (glowing)
    for (const side of [-1, 1]) {
      const eye = new THREE.Mesh(
        new THREE.SphereGeometry(0.035 * s, 10, 8),
        eyeMat
      );
      eye.position.set(side * 0.06 * s, 1.28 * s, -0.16 * s);
      g.add(eye);
    }

    // Neck
    const neck = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05 * s, 0.06 * s, 0.1 * s, 8),
      mat
    );
    neck.position.y = 1.12 * s;
    g.add(neck);

    // Arms with upper/lower
    for (const side of [-1, 1]) {
      const upperArm = new THREE.Mesh(
        new THREE.CylinderGeometry(0.055 * s, 0.045 * s, 0.3 * s, 8),
        mat
      );
      upperArm.position.set(side * 0.28 * s, 0.9 * s, -0.12 * s);
      upperArm.rotation.x = -0.5;
      upperArm.rotation.z = side * 0.1;
      upperArm.castShadow = false;
      g.add(upperArm);

      const lowerArm = new THREE.Mesh(
        new THREE.CylinderGeometry(0.04 * s, 0.035 * s, 0.28 * s, 8),
        mat
      );
      lowerArm.position.set(side * 0.32 * s, 0.62 * s, -0.3 * s);
      lowerArm.rotation.x = -0.7;
      lowerArm.castShadow = false;
      g.add(lowerArm);

      // Hand
      const hand = new THREE.Mesh(
        new THREE.SphereGeometry(0.04 * s, 8, 6),
        mat
      );
      hand.position.set(side * 0.32 * s, 0.42 * s, -0.4 * s);
      hand.scale.set(1, 0.6, 1.2);
      g.add(hand);

      // Torn sleeve
      const sleeve = new THREE.Mesh(
        new THREE.CylinderGeometry(0.065 * s, 0.055 * s, 0.12 * s, 8, 1, true),
        clothMat
      );
      sleeve.position.set(side * 0.28 * s, 1.0 * s, -0.08 * s);
      sleeve.rotation.x = -0.4;
      g.add(sleeve);
    }

    // Legs
    for (const side of [-1, 1]) {
      const upperLeg = new THREE.Mesh(
        new THREE.CylinderGeometry(0.065 * s, 0.055 * s, 0.35 * s, 8),
        clothMat
      );
      upperLeg.position.set(side * 0.1 * s, 0.3 * s, 0);
      upperLeg.castShadow = false;
      g.add(upperLeg);

      const lowerLeg = new THREE.Mesh(
        new THREE.CylinderGeometry(0.05 * s, 0.04 * s, 0.3 * s, 8),
        mat
      );
      lowerLeg.position.set(side * 0.1 * s, 0.0, 0);
      g.add(lowerLeg);
    }

    // Wounds
    for (let i = 0; i < 3; i++) {
      const wound = new THREE.Mesh(
        new THREE.CircleGeometry(0.03 * s + Math.random() * 0.02 * s, 8),
        woundMat
      );
      wound.position.set(
        (Math.random() - 0.5) * 0.3 * s,
        0.5 * s + Math.random() * 0.5 * s,
        -0.13 * s
      );
      g.add(wound);
    }

    // Exposed bone
    if (Math.random() > 0.5) {
      const bone = new THREE.Mesh(
        new THREE.CylinderGeometry(0.008 * s, 0.008 * s, 0.06 * s, 6),
        boneMat
      );
      bone.position.set(0.3 * s, 0.7 * s, -0.25 * s);
      g.add(bone);
    }

    // Torn cloth strips
    for (let i = 0; i < 3; i++) {
      const strip = new THREE.Mesh(
        new THREE.PlaneGeometry(0.04 * s, (0.08 + Math.random() * 0.1) * s),
        clothMat
      );
      const angle = Math.random() * Math.PI * 2;
      strip.position.set(Math.sin(angle) * 0.2 * s, 0.4 * s, Math.cos(angle) * 0.12 * s);
      strip.rotation.y = angle;
      g.add(strip);
    }

    g.position.set(x, y, z);
    if (crouched) {
      g.rotation.x = 0.4;
      g.position.y = -0.2;
    }
    g.userData.isZombie = true;
    this.add(g);

    // Eye glow light
    const eyeLight = new THREE.PointLight(0xffff00, 1.5, 4);
    eyeLight.position.set(x, 1.3 * s + y, z - 0.2);
    this.add(eyeLight);
  }

  private stopCutsceneAudio(): void {
    audioManager.stopBGM();
    if (this.activeAlarm) {
      this.activeAlarm.stop();
      this.activeAlarm = null;
    }
  }

  private cleanup(): void {
    for (const obj of this.objects) {
      this.scene.remove(obj);
      obj.traverse(child => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) child.material.dispose();
        }
      });
      if (obj instanceof THREE.Light) obj.dispose();
    }
    this.objects = [];
    this.dustParticles = null;
    if (this.composer) {
      this.composer.dispose();
    }
  }
}
